/* CA-16 Natulim DB 19/10/2026 — Actualizado a la versión ES + filtro de planes JUO de PT */
/**
 * NTL ATC Modal — replaces the cart drawer on add-to-cart with a cross-sell modal.
 * assets/ntl-atc-modal.js
 *
 * Rendered by: sections/ntl-atc-modal.liquid
 *
 * Flow
 *   1. Intercepts the product form submit in the CAPTURE phase, so the theme's
 *      <product-form> handler (which opens the drawer) never runs.
 *   2. Adds the line itself via /cart/add.js, refreshes the header count and
 *      the UpCart contents in the background.
 *   3. Opens this modal with the added item + the configured offer cards.
 *   4. Suppresses UpCart / theme drawer for as long as the modal is open.
 *
 * Offer cards are rendered server-side by the section (so variant swatch
 * metafields and selling plan allocations are available); this file only
 * filters, prices and adds them.
 */

if (!customElements.get('ntl-atc-modal')) {
  customElements.define(
    'ntl-atc-modal',
    class NtlAtcModal extends HTMLElement {
      /* ── Lifecycle ───────────────────────────────────────────────── */

      connectedCallback() {
        // Reparenting to <body> re-runs connectedCallback — init only once.
        if (this._initialized) return;

        // Only one controller may own the ATC interception.
        if (window.ntlAtcModalInstance && window.ntlAtcModalInstance !== this) {
          this.setAttribute('data-duplicate', '');
          return;
        }
        window.ntlAtcModalInstance = this;
        this._initialized = true;

        this.cfg = this.readJSON('.ntl-am__config') || {};
        this.offers = this.readJSON('.ntl-am__data') || [];
        this.offersById = Object.fromEntries(this.offers.map((o) => [o.bid, o]));
        this.pageProduct = this.readJSON('.ntl-am__page-product');

        this.dialog = this.querySelector('.ntl-am__dialog');
        this.cta = this.querySelector('.ntl-am__cta');
        this.errorEl = this.querySelector('.ntl-am__error');
        this.isOpen = false;
        this.isAdding = false;
        this.cartState = null;
        this.lastFocus = null;

        this.onKeydown = this.onKeydown.bind(this);
        this.sortAllFreqs();
        this.bindUI();
        // Antes de bindPrearm a propósito: los dos escuchan el clic en window /
        // captura, y el que se registra primero es el que corre primero. El
        // diferido tiene que poder cortar el añadido antes que nadie.
        this.bindDeferAdd();
        this.bindPrearm();
        this.bindUpcart();
        this.bindInterceptor();
        this.bindNetworkInterceptor();
        this.bindCartSuppression();
        this.bindPageVariantSync();
        this.escapeStackingContext();
        this.bindReopen();
        this.bindDuplicateGuard();
        this.warmUp();

        if (this.cfg.previewOpen && window.Shopify?.designMode) this.openPreview();
      }

      readJSON(selector) {
        const el = this.querySelector(selector);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('[ntl-am] bad JSON in', selector, e);
          return null;
        }
      }

      /* ── Add-to-cart interception ────────────────────────────────── */

      bindInterceptor() {
        if (!this.cfg.enabled) return;
        document.addEventListener('submit', (e) => this.onFormSubmit(e), true);
      }

      isTargetForm(form) {
        if (!(form instanceof HTMLFormElement)) return false;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return false;
        if (this.cfg.extraSelector) {
          try {
            if (form.matches(this.cfg.extraSelector) || form.closest(this.cfg.extraSelector)) return true;
          } catch (e) {
            /* invalid selector — ignore */
          }
        }
        if (this.cfg.scope === 'product_form_only') return !!form.closest('product-form');
        return true;
      }

      onFormSubmit(e) {
        const form = e.target;
        // Abierto por nosotros en el clic (tryInstantOpen con el mapa de la
        // landing): este submit ES el añadido que está esperando el modal.
        if (this.isOpen && !(this._instantOwn && this._pending)) return;
        if (!this.isTargetForm(form)) return;
        if (form.hasAttribute('data-ntl-am-skip')) return;
        if (!this.withinFrequencyCap()) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        this.handleAdd(form);
      }

      /* ── Duplicate guard on the page's own add-to-cart button ────── */

      /**
       * Once the page's product is in the cart, its add-to-cart button stops
       * adding and opens the cart instead — no more stacking friegasuelos.
       *
       * The label goes into a <span> of our own, appended to the button. See the
       * CSS for why neither a pseudo-element nor the button's own text works on
       * this store.
       */
      bindDuplicateGuard() {
        if (!this.cfg.enabled || !this.cfg.guardDuplicate || !this.pageProduct) return;
        this.fetchCart().then((cart) => {
          this.cartState = this.cartState || cart;
          this.refreshDuplicateGuard(cart);
        });
        // UpCart announces every cart change, ours included.
        window.addEventListener('aftersell-upcart:public-events:cart-updated', () => {
          this.fetchCart().then((cart) => this.refreshDuplicateGuard(cart));
        });
      }

      /** Buttons that add the page's product (not any other form on the page). */
      pageAtcButtons() {
        const ids = new Set((this.pageProduct?.variants || []).map((v) => String(v.id)));
        const out = [];
        document
          .querySelectorAll('form[action*="/cart/add"] [type="submit"], .product-form__submit')
          .forEach((btn) => {
            const form = btn.closest('form[action*="/cart/add"]');
            const vid = form?.querySelector('[name="id"]')?.value;
            if (vid && ids.has(String(vid))) out.push(btn);
          });
        return out;
      }

      isPageProductInCart(cart) {
        const pid = Number(this.pageProduct?.id);
        if (!pid) return false;
        return (cart?.items || []).some((li) => Number(li.product_id) === pid);
      }

      refreshDuplicateGuard(cart) {
        if (!this.cfg.guardDuplicate || !this.pageProduct) return;
        this._guardActive = this.isPageProductInCart(cart);
        const label = this.cfg.guardLabel || '';
        this.pageAtcButtons().forEach((btn) => {
          if (this._guardActive) {
            // Appended last on purpose: the LP's script rewrites the FIRST
            // `> span` it finds, which stays the theme's own.
            let slot = btn.querySelector(':scope > .ntl-am-guard-label');
            if (!slot) {
              slot = document.createElement('span');
              slot.className = 'ntl-am-guard-label';
              btn.appendChild(slot);
            }
            slot.textContent = label;
            btn.classList.add('ntl-am-guard-cta');
            if (label) btn.setAttribute('aria-label', label);
          } else {
            btn.classList.remove('ntl-am-guard-cta');
            btn.removeAttribute('aria-label');
          }
        });
      }

      /* ── UpCart integration (primary trigger) ────────────────────── */

      /**
       * UpCart hijacks the click on every add-to-cart button, cancels it and
       * POSTs to /cart/add.js?upcart=1&opens_cart=maybe itself. Verified on
       * natulim.com: NO submit event is ever dispatched, so a submit listener
       * can never see it. UpCart also re-wraps window.fetch and
       * XMLHttpRequest AFTER our script runs, wiping any patch of ours.
       *
       * The supported way in is its public event. It carries the added line, so
       * we get the product, variant, selling plan and price for free.
       * Using the raw event (not upcartSubscribeAddedToCart) makes this
       * independent of whether UpCart has finished loading before us.
       */
      bindUpcart() {
        if (!this.cfg.enabled || !this.cfg.interceptUpcart) return;
        window.addEventListener('aftersell-upcart:public-events:added-to-cart', (e) => {
          const item = e.detail?.item;
          if (item) this.handleUpcartAdd(item);
        });
      }

      async handleUpcartAdd(upcartItem) {
        // Deferred pack: this add was supposed to be blocked at the click. If it
        // landed anyway, adopt it instead of risking a second line on Continue.
        if (this._deferPending && !this._ownRequest) {
          this.adoptDeferredAdd(this.normalizeUpcartItem(upcartItem));
          return;
        }

        // The modal was already opened optimistically on the click — the real
        // item has landed, so reconcile instead of opening a second time.
        if (this._pending && this.isOpen) {
          this._pending = false;
          clearTimeout(this._pendingTimer);
          // Paint from the event immediately — its prices are already
          // discount-aware, so waiting for /cart.js here would only add latency
          // to the figure the shopper is waiting to see.
          this._lastItem = this.normalizeUpcartItem(upcartItem);
          // Landings: build frequency/variant controls from the product that was
          // actually added, before the header is painted from it.
          await this.ensureHeaderControls(this._lastItem);
          this.fillAddedItem(this._lastItem);
          this.exitAddingState();
          this.fetchCart().then((cart) => {
            this.cartState = cart;
            this.refreshCartCount(cart);
            // Repaint now that we can see every line of the product — this is
            // what catches the one-off + subscription split.
            this.applyCartLinePrice(this._lastItem);
            this.syncHeaderFreqs();
            this.syncHeaderOptions();
            this.applyHeaderDefaultPlan();
            this.updateTotals();
          });
          return;
        }

        if (!this.shouldTrigger(upcartItem.properties)) return;

        // Claim the screen before UpCart can render its drawer.
        this._claiming = true;
        this.suppressCart();
        try {
          // Open on the spot. Awaiting /cart.js first added a round trip during
          // which UpCart's drawer was the only thing on screen — the second half
          // of the flash. UpCart's cached cart covers what we need synchronously;
          // only fall back to the network when we truly need cart contents.
          let cart = this.upcartCartSnapshot();
          if (!cart && this.cfg.excludeInCart) cart = await this.fetchCart();

          const shown = this.open(this.normalizeUpcartItem(upcartItem), cart);
          if (!shown) {
            this.allowCart();
            return;
          }
          // Deliberately NOT from `cart`: UpCart's cached item_count lags the
          // add it just announced (measured 5 vs the real 6), so setting the
          // bubble from it would flash a wrong number. Product ids in that
          // snapshot ARE accurate, which is all the offer filter needs.
          // The same fetch also gives us the real line prices for the header.
          this.fetchCart().then((fresh) => {
            this.cartState = fresh;
            this.refreshCartCount(fresh);
            this.applyCartLinePrice(this._lastItem);
            this.syncHeaderFreqs();
            this.syncHeaderOptions();
            this.applyHeaderDefaultPlan();
          });
        } catch (err) {
          console.error('[ntl-am] upcart add failed:', err);
          this.allowCart();
        } finally {
          this._claiming = false;
        }
      }

      /** UpCart's in-memory cart, mapped to the Shopify field names we use. */
      upcartCartSnapshot() {
        try {
          const c = window.upcartGetCart?.();
          if (!c || !Array.isArray(c.items)) return null;
          return {
            item_count: c.items.reduce((n, i) => n + (i.quantity || 0), 0),
            items: c.items.map((i) => ({ product_id: i.productId, quantity: i.quantity })),
          };
        } catch (e) {
          return null;
        }
      }

      /** UpCart uses its own camelCase line shape — map it to Shopify's. */
      normalizeUpcartItem(u) {
        // LINE totals, never per-unit × quantity.
        //
        // The LP's discount is a fixed "one box free", and Shopify spreads a
        // fixed line discount across every unit: with 4 boxes at 9,00 the
        // per-unit final_price becomes 6,75 — which is exactly the wrong 6,75 €
        // that showed up in the header. The line total is the only figure that
        // matches the offer: 1 box → 0, 2 subs → 7,20, 2 one-offs → 9,00.
        const qty = u.quantity || u.quantityAdded || 1;
        const lineFinal =
          u.finalLinePrice != null ? u.finalLinePrice : (u.finalPrice ?? 0) * qty;
        const lineOriginal =
          u.originalLinePrice != null ? u.originalLinePrice : (u.originalPrice ?? 0) * qty;
        const variantTitle = (u.variantOptions || [])
          .filter((v) => v && v !== 'Default Title')
          .join(' / ');
        return {
          product_id: u.productId,
          id: u.variantId,
          // Needed to fetch the product at runtime on landings.
          handle: u.handle,
          quantity: qty,
          product_title: u.title,
          variant_title: variantTitle,
          image: u.image,
          featured_image: u.image ? { url: u.image, alt: u.title } : null,
          final_line_price: lineFinal,
          original_line_price: lineOriginal,
          selling_plan_allocation: u.sellingPlanAllocation
            ? { selling_plan: { name: u.sellingPlanAllocation.sellingPlan?.name } }
            : null,
          // Needed to tell a one-off line from a subscription line of the same
          // variant — Shopify keeps them as two separate cart lines.
          selling_plan_id: u.sellingPlanAllocation?.sellingPlan?.id || null,
          properties: u.properties || {},
          // UpCart's finalPrice already has discount codes applied — verified
          // against /cart.js on the LP: payload 640 === line.final_price 640,
          // with FRIEGA2026SUB in the allocation. So we can paint the real price
          // straight from the event and skip a /cart.js round trip entirely.
          _discountAware: true,
        };
      }

      /**
       * Shared gate for every trigger path. Stops double-opens when more than
       * one layer sees the same add, and skips our own merchandising blocks.
       */
      shouldTrigger(properties) {
        if (this._ownRequest || this.isOpen || this._claiming) return false;
        if (Date.now() - (this._openedAt || 0) < 1200) return false;
        if (/^ntl-/.test(properties?._source || '')) return false;
        return this.withinFrequencyCap();
      }

      /**
       * Several add-to-cart paths in this theme never submit a form — they POST
       * to /cart/add.js straight from JS (custom LP sections, Replo chunks,
       * collection quick-add). A submit listener can't see those, so we also
       * observe the network: let the request through untouched, then react to
       * its response. No double-add, works with any implementation.
       */
      bindNetworkInterceptor() {
        if (!this.cfg.enabled || !this.cfg.interceptNetwork) return;

        // UpCart's event already covers every add on this store, and wrapping
        // the network on top of other apps' wrappers is what broke add-to-cart
        // once (see below). When UpCart is available, this layer is redundant —
        // skip it rather than take the risk.
        if (this.cfg.interceptUpcart && typeof window.upcartSubscribeAddedToCart === 'function') {
          return;
        }

        const self = this;

        // Plain assignment, capturing the previous fetch BY VALUE.
        //
        // Do NOT install an accessor here. An earlier version did, and EasyGift
        // (cdn.506.io) read window.fetch through the getter, then assigned its
        // own wrapper around what it had read — so our wrapper called theirs and
        // theirs called ours: "RangeError: Maximum call stack size exceeded" on
        // every add-to-cart. Capturing by value keeps the chain finite: whoever
        // wraps us later calls us, and we call what existed before us.
        const origFetch = window.fetch;
        if (typeof origFetch === 'function' && !origFetch.__ntlAmPatched) {
          const patched = function (input, init) {
            const promise = origFetch.apply(this, arguments);
            let url = '';
            let method = 'GET';
            try {
              url = typeof input === 'string' ? input : input?.url || '';
              method = (init?.method || input?.method || 'GET').toUpperCase();
            } catch (e) {
              return promise;
            }
            if (method !== 'POST' || !self.isAddUrl(url)) return promise;
            return promise.then((res) => {
              if (res && res.ok) {
                res
                  .clone()
                  .json()
                  .then((data) => self.onExternalAdd(data))
                  .catch(() => {});
              }
              return res;
            });
          };
          patched.__ntlAmPatched = true;
          window.fetch = patched;
        }

        const proto = XMLHttpRequest.prototype;
        if (!proto.open.__ntlAmPatched) {
          const origOpen = proto.open;
          const origSend = proto.send;

          const openPatched = function (method, url) {
            this.__ntlAm = { method: String(method || '').toUpperCase(), url: String(url || '') };
            return origOpen.apply(this, arguments);
          };
          openPatched.__ntlAmPatched = true;
          proto.open = openPatched;

          proto.send = function () {
            const meta = this.__ntlAm;
            if (meta && meta.method === 'POST' && self.isAddUrl(meta.url)) {
              this.addEventListener('load', () => {
                if (this.status < 200 || this.status >= 300) return;
                try {
                  self.onExternalAdd(JSON.parse(this.responseText));
                } catch (e) {
                  /* not JSON — ignore */
                }
              });
            }
            return origSend.apply(this, arguments);
          };
        }
      }

      isAddUrl(url) {
        const path = String(url).split('?')[0].split('#')[0];
        return /\/cart\/add(\.js)?$/.test(path);
      }

      /** Entry point for adds we did not initiate ourselves. */
      async onExternalAdd(data) {
        if (!data || data.status) return;
        const item = (data.items && data.items[0]) || (data.id ? data : null);
        if (!item) return;
        // Same safety net as the UpCart path: a deferred add that got through
        // is adopted, never added a second time (see adoptDeferredAdd).
        if (this._deferPending && !this._ownRequest) {
          this.adoptDeferredAdd(item);
          return;
        }
        if (!this.shouldTrigger(item.properties)) return;

        // Claim the screen before the other script gets to open its drawer.
        this._claiming = true;
        this.suppressCart();
        try {
          const cart = await this.fetchCart();
          this.refreshCartCount(cart);
          const shown = this.open(item, cart);
          if (!shown) this.allowCart();
        } finally {
          this._claiming = false;
        }
      }

      async handleAdd(form) {
        const submitBtn =
          form.querySelector('[type="submit"]:not([disabled])') ||
          document.querySelector(`[type="submit"][form="${form.id}"]`);
        const spinner = submitBtn?.querySelector('.loading__spinner');
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.classList.add('loading');
          spinner?.classList.remove('hidden');
        }

        this._ownRequest = true;
        try {
          const body = new FormData(form);
          body.append('sections', 'cart-icon-bubble');
          body.append('sections_url', window.location.pathname);

          const res = await fetch(this.route('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const data = await res.json();

          if (data.status) {
            // Real add-to-cart error — hand control back to the theme.
            this.abortInstant();
            this.releaseToTheme(form, data);
            return;
          }

          this.renderSections(data.sections);
          const item = (data.items && data.items[0]) || data;
          // El modal ya está abierto desde el clic: se rellena con la línea real
          // en lugar de abrir otra vez (ni esperar a /cart.js para pintarlo).
          if (this._instantOwn && this._pending && this.isOpen) {
            await this.reconcileInstant(item);
            return;
          }
          const cart = await this.fetchCart();
          this.refreshCartCount(cart);
          window.upcartRefreshCart?.();

          const shown = this.open(item, cart);
          if (!shown) this.openNativeCart();
        } catch (err) {
          console.error('[ntl-am] add failed:', err);
          this.abortInstant();
          this.releaseToTheme(form);
        } finally {
          this._ownRequest = false;
          if (submitBtn) {
            submitBtn.classList.remove('loading');
            submitBtn.removeAttribute('aria-disabled');
            spinner?.classList.add('hidden');
          }
        }
      }

      /**
       * El añadido que abrió el modal en el clic ha llegado: se pinta la línea
       * real sobre lo que ya hay en pantalla. Es el mismo reconcile que hace
       * handleUpcartAdd con el evento de UpCart, para el caso de una landing
       * cuyo botón añade por nuestro propio submit.
       */
      async reconcileInstant(item) {
        this._instantOwn = false;
        this._pending = false;
        clearTimeout(this._pendingTimer);
        this._lastItem = item;
        // `cartState` es el de ANTES del añadido (snapshot de UpCart o el que dejó
        // warmUp), sin la línea que acaba de entrar: con él, el umbral de envío
        // gratis se leía como no alcanzado y las notas parpadeaban «de pago» →
        // «gratis» al llegar /cart.js. Se cuenta ya la línea nueva.
        this.cartState = this.cartWithLine(this.cartState, item);
        await this.ensureHeaderControls(item);
        this.fillAddedItem(item);
        this.exitAddingState();
        window.upcartRefreshCart?.();
        this.fetchCart().then((cart) => {
          this.cartState = cart;
          this.refreshCartCount(cart);
          this.applyCartLinePrice(this._lastItem);
          this.syncHeaderFreqs();
          this.syncHeaderOptions();
          this.applyHeaderDefaultPlan();
          this.updateTotals();
        });
      }

      /** El carrito que ya conocemos más la línea recién añadida (si no está ya dentro). */
      cartWithLine(cart, item) {
        const base = cart || { items: [], item_count: 0, total_price: 0 };
        const items = base.items || [];
        if (item && item.key && items.some((li) => li.key === item.key)) return base;
        return {
          ...base,
          items: [...items, item],
          item_count: (Number(base.item_count) || 0) + (Number(item?.quantity) || 1),
          total_price: (Number(base.total_price) || 0) + (Number(item?.final_line_price) || 0),
        };
      }

      /** El añadido falló con el modal ya abierto: se cierra para que el tema muestre su error. */
      abortInstant() {
        if (!this._instantOwn) return;
        this._instantOwn = false;
        if (this.isOpen) this.close();
      }

      /**
       * Something went wrong on our side (network, sold out, …). Re-submit with
       * a skip flag so the theme's own <product-form> handler takes over and
       * shows its native error / drawer.
       */
      releaseToTheme(form, data) {
        if (data?.description) console.warn('[ntl-am]', data.description);
        form.setAttribute('data-ntl-am-skip', '');
        form.requestSubmit ? form.requestSubmit() : form.submit();
        setTimeout(() => form.removeAttribute('data-ntl-am-skip'), 1500);
      }

      /* ── Cart helpers ────────────────────────────────────────────── */

      route(path) {
        const root = window.Shopify?.routes?.root || '/';
        return root + path;
      }

      async fetchCart() {
        try {
          const res = await this.withTimeout(
            fetch(this.route('cart.js'), { headers: { Accept: 'application/json' } })
          );
          return await this.withTimeout(res.json());
        } catch (e) {
          return null;
        }
      }

      /**
       * Techo para cualquier ida y vuelta al carrito.
       *
       * Un `await` sin techo es lo que deja el CTA girando para siempre: el
       * añadido entra en el carrito y la promesa NO se resuelve nunca. En esta
       * tienda `window.fetch` lo envuelven UpCart, EasyGift y compañía (ver
       * bindNetworkInterceptor), así que la respuesta que nos llega ya ha pasado
       * por código que no controlamos y que puede quedarse a medias.
       *
       * Mismo criterio que `instant_open_timeout` con la apertura optimista: se
       * pone un límite y se decide con lo que se sepa. La petición NO se aborta
       * a propósito — si va a entrar en el carrito, que entre; quien llama lo
       * comprueba luego contra /cart.js (cartContains / findLandedLine).
       */
      get netTimeout() {
        return Math.max(6000, Number(this.cfg.instantOpenTimeout) || 0);
      }

      withTimeout(promise, ms) {
        let timer;
        const cap = new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('ntl-am-timeout')), ms || this.netTimeout);
        });
        return Promise.race([promise, cap]).finally(() => clearTimeout(timer));
      }

      /** La línea del carrito que corresponde a una variante + plan pedidos. */
      lineFor(cart, variantId, planId) {
        return (cart?.items || []).find(
          (li) =>
            Number(li.variant_id ?? li.id) === Number(variantId) &&
            String(li.selling_plan_allocation?.selling_plan?.id || '') === String(planId || '')
        );
      }

      /**
       * ¿Entró de verdad lo que acabamos de pedir? Cuando la respuesta del POST
       * no llega, el carrito es la única fuente de verdad — y es lo que evita
       * tanto el error en falso como el añadido por duplicado al reintentar.
       */
      async cartContains(items) {
        const cart = await this.fetchCart();
        if (!cart?.items) return false;
        return items.every((it) => !!this.lineFor(cart, it.id, it.selling_plan));
      }

      /** Igual, pero devolviendo la línea (la necesita el añadido diferido). */
      async findLandedLine(variantId, planId) {
        const cart = await this.fetchCart();
        const line = this.lineFor(cart, variantId, planId);
        if (!line) return null;
        this.cartState = cart;
        return line;
      }

      renderSections(sections) {
        if (!sections) return;
        const html = sections['cart-icon-bubble'];
        const target = document.getElementById('cart-icon-bubble');
        if (!html || !target) return;
        try {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const inner = parsed.querySelector('.shopify-section')?.innerHTML;
          if (inner) target.innerHTML = inner;
        } catch (e) {
          /* non-fatal */
        }
      }

      refreshCartCount(cart) {
        if (!cart) return;
        // Every cart change is a chance for the guard to switch on or off, and
        // every path that mutates the cart lands here.
        this.refreshDuplicateGuard(cart);
        const count = cart.item_count;
        document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
          const numeric = bubble.querySelector('span[aria-hidden="true"], span:not(.visually-hidden)');
          if (numeric) numeric.textContent = count;
          bubble.hidden = count === 0;
        });
      }

      /** Opens whatever cart UI the store actually uses. */
      openNativeCart() {
        this.allowCart();
        if (typeof window.upcartOpenCart === 'function') {
          // The only call allowed past guardNativeCartOpener().
          this._allowNativeOpen = true;
          try {
            window.upcartOpenCart();
          } finally {
            this._allowNativeOpen = false;
          }
          return;
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.open) {
          drawer.open();
          return;
        }
        window.location.href = this.route('cart');
      }

      /* ── Cart drawer suppression ─────────────────────────────────── */

      bindCartSuppression() {
        if (!this.cfg.suppressCart) return;
        window.addEventListener('aftersell-upcart:public-events:drawer-opened', () => {
          if (this.isOpen || this._claiming || this._prearmed) window.upcartCloseCart?.();
        });
        this.guardNativeCartOpener();
      }

      /**
       * Stops the drawer from opening in the first place.
       *
       * The theme's global.js deliberately calls window.upcartOpenCart() from a
       * click listener on every add-to-cart button (UpCart's own auto-open is
       * turned off in the app, so the theme does it by hand). That is the drawer
       * that appeared on add. Hiding it after the fact left a flash, so instead
       * the opener itself becomes a no-op while the modal owns the flow — only
       * our openNativeCart() is allowed through.
       *
       * UpCart loads async, so retry until the function exists, and re-guard if
       * something replaces it later.
       */
      guardNativeCartOpener() {
        const install = () => {
          const orig = window.upcartOpenCart;
          if (typeof orig !== 'function' || orig.__ntlAmGuarded) return typeof orig === 'function';
          const guarded = (...args) => {
            if (!this._allowNativeOpen && (this.isOpen || this._claiming || this._prearmed)) return undefined;
            return orig.apply(window, args);
          };
          guarded.__ntlAmGuarded = true;
          window.upcartOpenCart = guarded;
          return true;
        };

        if (install()) return;
        let tries = 0;
        const timer = setInterval(() => {
          if (install() || ++tries > 40) clearInterval(timer);
        }, 250);
      }

      /**
       * Just the CSS class. UpCart renders its whole drawer inside the shadow
       * root of #upCart, and `visibility` inherits through shadow boundaries, so
       * hiding the host hides the drawer and its backdrop in one go.
       */
      armSuppression() {
        if (!this.cfg.suppressCart) return;
        document.documentElement.classList.add('ntl-am-suppress-cart');
        this.lockScroll();
      }

      /**
       * Takes ownership of the page scroll lock, measuring the scrollbar width
       * for the no-scrollbar-gutter fallback. Held from the click through to the
       * modal closing, so the page only transitions once instead of jittering
       * right-then-left as UpCart locks and unlocks underneath us.
       */
      lockScroll() {
        if (this._locked) return;
        const root = document.documentElement;
        const width = window.innerWidth - root.clientWidth;
        root.style.setProperty('--ntl-am-sbw', (width > 0 ? width : 0) + 'px');
        root.classList.add('ntl-am-lock');
        this._locked = true;
      }

      unlockScroll() {
        if (!this._locked) return;
        document.documentElement.classList.remove('ntl-am-lock');
        this._locked = false;
      }

      suppressCart() {
        if (!this.cfg.suppressCart) return;
        this.armSuppression();
        window.upcartCloseCart?.();
        document.querySelector('cart-drawer')?.classList.remove('active', 'animate');
      }

      /**
       * Kills the "drawer flashes open then closes" effect.
       *
       * Suppressing only once the add-to-cart response lands is a race we lose:
       * UpCart has already started its open animation by then. Arming on the
       * click instead is deterministic — the host is hidden from the very first
       * frame, so the drawer never becomes visible at all. Measured on the LP:
       * #upCart visibility "hidden" across every sample from 0 to 1600 ms.
       *
       * Disarms itself if no add materialises, so a click that turns out not to
       * be an add-to-cart never leaves the cart stuck invisible.
       */
      bindPrearm() {
        if (!this.cfg.enabled || !this.cfg.suppressCart || !this.cfg.prearmOnClick) return;
        let selector = 'form[action*="/cart/add"] [type="submit"], .product-form__submit, [name="add"]';
        if (this.cfg.extraSelector) selector += ', ' + this.cfg.extraSelector;

        // On `window`, not `document`: the theme's global.js has its own
        // capture-phase click listener on document that opens the UpCart drawer,
        // and it is registered in the <head> — before our deferred script — so we
        // cannot win by registration order. Capture propagation starts at Window
        // and only then reaches Document, so listening one level up is what lets
        // us mark the click as ours before global.js acts on it.
        window.addEventListener(
          'click',
          (e) => {
            if (this.isOpen || this._claiming) return;
            let hit = null;
            try {
              hit = e.target.closest(selector);
            } catch (err) {
              return;
            }
            if (!hit) return;

            // Product already in the cart: kill the add entirely and just open
            // the cart. This listener is on `window` in capture, i.e. the first
            // step of the event path, so stopping it here also stops the theme's
            // global.js opener, UpCart's own hijack and the theme's product-form.
            if (this._guardActive && this.pageAtcButtons().includes(hit)) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this.track('guard_open_cart', []);
              this.openNativeCart();
              return;
            }

            if (!this.withinFrequencyCap()) return;

            this._prearmed = true;
            this.armSuppression();
            clearTimeout(this._prearmTimer);
            this._prearmTimer = setTimeout(() => {
              this._prearmed = false;
              if (!this.isOpen && !this._claiming) this.allowCart();
            }, 3000);

            this.tryInstantOpen(hit);
          },
          true
        );
      }

      /**
       * Opens on the click instead of on the response.
       *
       * The add itself takes ~687 ms on this store (measured click →
       * added-to-cart), all of it UpCart's POST round trip. Waiting for it is
       * what made the modal feel slow, and nothing in the modal actually needs
       * the response: the header comes from the page product and the offers are
       * rendered server-side.
       *
       * Only fires when the clicked form is selling the product we already have
       * data for — otherwise (collection quick-add, an unrelated form) we'd show
       * the wrong product, so we fall back to waiting for the real event.
       */
      tryInstantOpen(trigger) {
        if (!this.cfg.instantOpen || this.isOpen) return;

        const form = trigger.closest('form[action*="/cart/add"]');
        const variantId = form?.querySelector('[name="id"]')?.value;
        let presumed = null;
        const known =
          !!this.pageProduct &&
          (this.pageProduct.variants || []).some((v) => String(v.id) === String(variantId));
        if (known) {
          presumed = {
            product_id: this.pageProduct.id,
            id: variantId,
            properties: {},
          };
        } else {
          // Landing: no hay producto de página, así que el id de variante del
          // formulario se resuelve con el mapa que emite Liquid. Aquí el
          // añadido lo hace nuestro propio submit (no UpCart), por eso se marca
          // el modal como «abierto por nosotros»: onFormSubmit lo deja pasar y
          // handleAdd rellena el modal con la respuesta en vez de abrirlo.
          const target = this.instantTargetFor(variantId);
          if (!target) return;
          presumed = {
            product_id: Number(target.p) || null,
            id: Number(variantId),
            handle: target.h || '',
            properties: {},
          };
          this._instantOwn = true;
        }

        this._claiming = true;
        // Sin el snapshot de UpCart (aún sin hidratar) la exclusión de ofertas ya
        // en el carrito no tendría con qué trabajar: en la landing se tira del
        // carrito que `warmUp` dejó cacheado.
        const shown = this.open(
          presumed,
          this.upcartCartSnapshot() || (this._instantOwn ? this.cartState : null)
        );
        this._claiming = false;
        if (!shown) {
          this._instantOwn = false;
          this.allowCart();
          return;
        }

        this.markPricePending();
        this.enterAddingState();

        // If no add ever lands, the add failed (sold out, network). Step aside
        // and let the theme / UpCart show their own error.
        this._pending = true;
        clearTimeout(this._pendingTimer);
        this._pendingTimer = setTimeout(() => {
          if (this._pending && this.isOpen) this.close();
        }, this.cfg.instantOpenTimeout || 6000);
      }

      /* ── Deferred add (packs get configured BEFORE they land) ─────── */

      /**
       * Un pack de dos pasos no se añade al pulsar: primero se configura.
       *
       * Hoy el clic añade y el modal abre después, así que cerrarlo sin
       * configurar deja el pack en el carrito tal como cayó. Para algo que
       * todavía tiene fragancia y frecuencia por decidir eso está al revés: se
       * elige primero y solo «Continuar» lo mete en el carrito.
       *
       * El clic se corta en window/captura, la primera parada del recorrido del
       * evento — que es lo que para de golpe el hijack de UpCart, el opener de
       * global.js y el submit del propio formulario. Mismo mecanismo que ya usa
       * el guard de duplicados unas líneas más arriba.
       *
       * Sin `pack_defer_add` no se registra ni el listener: las demás instancias
       * (friegasuelos, PDPs) no ven una sola línea de esto.
       */
      bindDeferAdd() {
        if (!this.cfg.enabled || !this.cfg.packDeferAdd) return;
        let selector = 'form[action*="/cart/add"] [type="submit"], .product-form__submit, [name="add"]';
        if (this.cfg.extraSelector) selector += ', ' + this.cfg.extraSelector;

        window.addEventListener(
          'click',
          (e) => {
            if (this.isOpen || this._claiming || this._deferPending) return;
            let hit = null;
            try {
              hit = e.target.closest(selector);
            } catch (err) {
              return;
            }
            if (!hit) return;
            const form = hit.closest('form[action*="/cart/add"]');
            // Sin formulario no hay payload que aplazar, y con la marca de
            // "déjalo pasar" puesta el añadido es del tema (ver releaseToTheme).
            if (!form || form.hasAttribute('data-ntl-am-skip')) return;

            const payload = this.readFormPayload(form);
            const target = this.deferTargetFor(payload.id);
            if (!target) return;
            // Con el modal capado por frecuencia no habría dónde configurar el
            // pack: mejor dejar pasar el añadido de siempre que comerse el clic.
            if (!this.withinFrequencyCap()) return;

            e.preventDefault();
            e.stopImmediatePropagation();
            this.openDeferred(payload, target, form);
          },
          true
        );
      }

      /** Lo que el formulario de la landing iba a enviar, tal cual. */
      readFormPayload(form) {
        const data = new FormData(form);
        const properties = {};
        data.forEach((value, key) => {
          const m = /^properties\[(.+)\]$/.exec(key);
          if (m) properties[m[1]] = value;
        });
        return {
          id: Number(data.get('id')) || 0,
          // La landing puede añadir más de una unidad; lo que mande el
          // formulario es lo que acabará en el carrito al pulsar «Continuar».
          quantity: Math.max(1, Number(data.get('quantity')) || 1),
          selling_plan: Number(data.get('selling_plan')) || null,
          properties,
        };
      }

      /**
       * ¿La variante pertenece a un pack de dos pasos? Devuelve su producto
       * (`{p: id, h: handle}`) o null.
       *
       * El mapa lo emite Liquid desde `two_steps_products`, porque en el clic hay
       * que decidirlo SIN red: del formulario solo sale el id de variante, y
       * `twoStepsProducts` habla de productos.
       */
      deferTargetFor(variantId) {
        const map = this.cfg.deferVariants || {};
        return (variantId && map[String(variantId)]) || null;
      }

      /** Lo mismo para los productos que abren al instante (`instant_products`). */
      instantTargetFor(variantId) {
        const map = this.cfg.instantVariants || {};
        return (variantId && map[String(variantId)]) || null;
      }

      /**
       * Calienta lo que el modal va a necesitar, con la página ya en reposo.
       *
       * Dos esperas medidas en la LP de Bolas, en móvil con red lenta:
       *  · las imágenes del modal son `loading="lazy"` dentro de un contenedor
       *    oculto, así que el navegador no las pide hasta que el modal se ve y
       *    se abre con cajas grises ~0,6 s;
       *  · la ficha del pack (/products/<handle>.js) solo se pedía al abrir, y
       *    de ella cuelgan los selectores y la foto: hasta 4,6 s en el peor caso.
       * Aquí se piden antes. `fetchProduct` cachea por handle, así que al abrir
       * ya está. Solo con el ajuste `warm_up`: cuesta unos 20 KB por página.
       */
      warmUp() {
        if (!this.cfg.warmUp) return;
        const run = () => {
          this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.fetchPriority = 'low';
            img.loading = 'eager';
          });
          Object.values(this.cfg.packComponents || {}).forEach((pc) => {
            if (pc && pc.img) new Image().src = pc.img;
          });
          // El carrito de antes del clic: lo usa la apertura instantánea para no
          // ofrecer lo que ya está dentro (ver tryInstantOpen).
          if (!this.cartState) {
            this.fetchCart().then((cart) => {
              if (cart && !this.cartState) this.cartState = cart;
            });
          }
          const handles = new Set();
          [this.cfg.deferVariants, this.cfg.instantVariants].forEach((map) => {
            Object.values(map || {}).forEach((t) => t && t.h && handles.add(t.h));
          });
          handles.forEach((h) => {
            this.fetchProduct(h).then((p) => {
              if (!p) return;
              // La misma URL que pinta paintAddedIdentity/fillAddedItem.
              const hp = this.toHeaderProduct(p, {});
              const v = (hp.variants || [])[0];
              if (v && v.img) new Image().src = this.sized(v.img, 220);
            });
          });
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
        else setTimeout(run, 2000);
      }

      /** El pack que se está configurando, con forma de línea de carrito. */
      pendingItem() {
        const p = this._pendingAdd || {};
        return {
          product_id: p.productId,
          id: p.id,
          handle: p.handle,
          quantity: p.quantity,
          properties: p.properties || {},
          selling_plan_id: p.selling_plan || null,
        };
      }

      /** Abre el modal en modo configuración, con el añadido en la recámara. */
      async openDeferred(payload, target, form) {
        this._pendingAdd = {
          id: payload.id,
          quantity: payload.quantity,
          selling_plan: payload.selling_plan,
          properties: payload.properties,
          productId: Number(target.p) || null,
          handle: target.h || '',
          form,
        };
        this._deferPending = true;

        this._claiming = true;
        // El precio bueno no existe hasta leer el producto (una petición), así
        // que el esqueleto de siempre en vez de un 0,00 € que se corrige solo.
        this.markPricePending();
        const shown = this.open(this.pendingItem(), this.upcartCartSnapshot());
        this._claiming = false;

        if (!shown) {
          // Nos hemos comido el clic y no hay modal que enseñar (sin ofertas y
          // con `hide_when_empty`): el añadido de siempre, para que el cliente
          // no se quede sin nada. Si falla, el formulario del tema toma el
          // relevo y enseña su propio error.
          this._deferPending = false;
          const item = await this.submitPendingAdd();
          const pendingForm = this._pendingAdd?.form;
          this._pendingAdd = null;
          if (item) this.openNativeCart();
          else if (pendingForm) this.releaseToTheme(pendingForm);
          else this.allowCart();
          return;
        }

        this.track('pack_configure', []);
        // En segundo plano: el carrito real es el que sabe del contador de la
        // cabecera y del umbral de envío gratis.
        this.fetchCart().then((cart) => {
          if (!cart) return;
          this.cartState = cart;
          this.refreshCartCount(cart);
          this.updateTotals();
        });
      }

      /**
       * El POST del pack pendiente. Devuelve la línea añadida, o null cuando
       * falla — y en ese caso el carrito se queda exactamente como estaba, así
       * que reintentar es seguro.
       */
      async submitPendingAdd() {
        const p = this._pendingAdd;
        if (!p) return null;
        const payload = {
          id: Number(p.id),
          quantity: Number(p.quantity) || 1,
          // Las propiedades del formulario se respetan; el `_source` con prefijo
          // `ntl-` es lo que impide que nuestro propio añadido vuelva a abrir el
          // modal (ver shouldTrigger), y al empezar por «_» Shopify no lo enseña.
          properties: { ...(p.properties || {}), _source: 'ntl-atc-modal-pack' },
        };
        if (p.selling_plan) payload.selling_plan = Number(p.selling_plan);

        this._ownRequest = true;
        try {
          const res = await this.withTimeout(
            fetch(this.route('cart/add.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [payload] }),
            })
          );
          const data = await this.withTimeout(res.json());
          if (!res.ok || data.status) {
            this.setError(data.description || this.cfg.errorGeneric || '');
            return null;
          }
          const item = (data.items && data.items[0]) || data;
          // El handle lo necesita reopen_on_back para reconstruir los controles.
          if (item && !item.handle) item.handle = p.handle;
          return item;
        } catch (err) {
          // La respuesta no ha llegado (techo de `withTimeout` o red), pero el
          // POST puede haber entrado igual. Se mira el carrito antes de dar el
          // añadido por fallido: si el pack está dentro se sigue con su línea
          // —«Continuar» no puede quedarse colgado con el pack ya comprado— y
          // solo si no está se enseña el error.
          const landed = await this.findLandedLine(payload.id, payload.selling_plan);
          if (landed) {
            if (!landed.handle) landed.handle = p.handle;
            return landed;
          }
          console.error('[ntl-am] deferred add failed:', err);
          this.setError(this.cfg.errorGeneric || '');
          return null;
        } finally {
          this._ownRequest = false;
        }
      }

      /**
       * «Continuar» en modo diferido: AQUÍ entra el pack en el carrito, con la
       * variante, la cantidad y el plan que el cliente acaba de elegir.
       *
       * Desde la respuesta el modal vuelve a su vía de siempre —la misma que usa
       * el añadido de UpCart—: repintar desde la línea real y pasar al paso 2 con
       * la venta cruzada. Si el añadido falla no se avanza: el error se pinta en
       * `.ntl-am__error` y el cliente puede corregir la elección o reintentar.
       */
      async commitPendingAdd() {
        if (this._committing || !this._pendingAdd) return;
        this._committing = true;
        // El mismo cerrojo que usa swapHeaderLine: con la petición en vuelo, los
        // selectores no aceptan cambios, o lo que se añadió y lo que se ve
        // dejarían de ser la misma cosa.
        this._swapping = true;
        const btn = this.querySelector('.ntl-am__continue');
        if (btn) btn.disabled = true;
        this.setError('');
        this.enterAddingState();

        const item = await this.submitPendingAdd();
        this._committing = false;
        this._swapping = false;
        if (btn) btn.disabled = false;

        if (!item) {
          this.exitAddingState();
          return;
        }

        // A partir de aquí manda el carrito, no la selección pendiente.
        this._deferPending = false;
        this._pendingAdd = null;
        this._lastItem = item;
        this.exitAddingState();
        this.track('pack_add_to_cart', []);
        window.upcartRefreshCart?.();

        // Cerrado mientras el añadido estaba en vuelo: el pack entra igual —el
        // cliente lo pidió—, pero no hay modal que repintar ni paso al que
        // pasar. Queda el carrito y su contador.
        if (!this.isOpen) {
          this.fetchCart().then((cart) => {
            this.cartState = cart;
            this.refreshCartCount(cart);
          });
          return;
        }

        this.fillAddedItem(item);
        this.goToStep(2);

        // Igual que en el añadido de UpCart: el carrito real refina el precio
        // (códigos de descuento, la misma variante en compra única Y en
        // suscripción) y pone al día el contador de la cabecera.
        this.fetchCart().then((cart) => {
          this.cartState = cart;
          this.refreshCartCount(cart);
          this.applyCartLinePrice(this._lastItem);
          this.syncHeaderFreqs();
          this.syncHeaderOptions();
          this.paintAddedIdentity();
          this.paintPackSummary();
          this.updateTotals();
        });
      }

      /**
       * Red de seguridad: el añadido diferido tenía que estar cortado y ha
       * llegado igual (otra capa se quedó con el clic antes que nosotros).
       *
       * En vez de arriesgar una segunda línea al pulsar «Continuar», se adopta lo
       * que ha entrado y el modal vuelve al comportamiento de siempre: el pack ya
       * está dentro y «Continuar» solo pasa al paso 2.
       */
      async adoptDeferredAdd(item) {
        // Nuestro propio añadido, en vuelo o recién resuelto: `_ownRequest` se
        // suelta en el `finally` de submitPendingAdd, un microtask ANTES de que
        // commitPendingAdd retome y baje `_deferPending`. En esa rendija el
        // evento de UpCart entraría aquí y adoptaría la línea que ya estamos
        // adoptando, duplicando el repintado (y dejando `_pendingAdd` en null a
        // media transacción). `_committing` sigue en alto durante toda esa
        // rendija, así que cierra la carrera de un tiro.
        if (this._committing) return;
        this._deferPending = false;
        this._pendingAdd = null;
        this._lastItem = item;
        // La variante que manda es la que ha entrado en el carrito, no la que el
        // cliente tuviera a medio elegir.
        this.headerVariantId = Number(item.id) || this.headerVariantId;
        this.fillAddedItem(item);
        const cart = await this.fetchCart();
        if (!cart) return;
        this.cartState = cart;
        this.refreshCartCount(cart);
        this.applyCartLinePrice(item);
        this.syncHeaderFreqs();
        this.syncHeaderOptions();
        this.paintPackSummary();
        this.updateTotals();
      }

      /**
       * Cambio de variante en modo diferido: solo la selección pendiente. No hay
       * línea de carrito que reescribir, así que tampoco hay red ni riesgo de
       * dejar el pack duplicado (comparar con swapHeaderLine).
       */
      setPendingVariant(variantId) {
        if (!this._pendingAdd) return;
        this.headerVariantId = Number(variantId);
        this._pendingAdd.id = Number(variantId);
        this.ensurePendingPlan();
        this.syncHeaderOptions();
        this.paintAddedIdentity();
        this.paintPendingPrice();
        this.paintPackSummary();
      }

      /**
       * Los planes se declaran por variante: si la recién elegida no tiene el
       * que estaba marcado, Shopify rechazaría el añadido con un 422 al pulsar
       * «Continuar». Se cae a una píldora que sí exista (compra única, si está).
       */
      ensurePendingPlan() {
        const planId = this._pendingAdd?.selling_plan;
        if (!planId) return;
        const wrap = this.querySelector('.ntl-am__added-freqs');
        if (!wrap) return;
        const variants = this.headerProduct?.variants || [];
        const v = variants.find((x) => Number(x.id) === Number(this.headerVariantId));
        const plans = (v && v.plans) || [];
        if (plans.some((id) => String(id) === String(planId))) return;

        const pills = [...wrap.querySelectorAll('.ntl-am__added-freq')];
        const fallback = pills.find((p) => !p.dataset.planId) || pills[0] || null;
        const nextId = fallback && fallback.dataset.planId ? Number(fallback.dataset.planId) : null;
        this._pendingAdd.selling_plan = nextId;
        pills.forEach((p) => p.classList.toggle('is-selected', p === fallback));
      }

      /** Cambio de frecuencia en modo diferido. Tampoco toca el carrito. */
      setPendingPlan(planId) {
        if (!this._pendingAdd) return;
        this._pendingAdd.selling_plan = planId ? Number(planId) : null;
        this.paintPendingPrice();
        this.paintPackSummary();
      }

      /**
       * El precio de la cabecera ANTES de que exista línea de carrito.
       *
       * En modo diferido no hay nada añadido, así que las cifras salen del
       * producto que ya leyó `ensureHeaderControls`: el precio final es el de la
       * asignación del plan elegido (`per_delivery_price`, o el de la variante en
       * compra única) y el tachado, el precio sin suscripción de esa misma
       * variante — el mismo criterio que aplica `headerSubReference` con el
       * carrito delante. Por eso se delega en `paintHeaderPrice`: la píldora
       * «-N% Descuento suscripción», el total del CTA y el {precio} del enlace de
       * salida se pintan igual sin saber de dónde salen las cifras.
       */
      paintPendingPrice() {
        if (!this._deferPending) return false;
        const wrap = this.querySelector('.ntl-am__added');
        const variants = this.headerProduct?.variants || [];
        const v = variants.find((x) => Number(x.id) === Number(this.headerVariantId)) || variants[0];
        if (!v) {
          // Sin producto no hay precio que pintar; soltar el esqueleto es mejor
          // que dejarlo girando para siempre.
          wrap?.classList.remove('is-price-pending');
          return false;
        }
        const planId = this._pendingAdd?.selling_plan;
        const perDelivery = planId ? (v.planPrices || {})[String(planId)] : null;
        const unit = perDelivery != null ? Number(perDelivery) : Number(v.p) || 0;
        const qty = Math.max(1, Number(this._pendingAdd?.quantity) || 1);
        // `paintHeaderPrice` habla en totales de línea y los reparte entre las
        // unidades (headerAmount), así que se le da el total y devuelve el
        // precio de UN pack, que es lo que la cabecera anuncia.
        return this.paintHeaderPrice(unit * qty, unit * qty, !!planId, qty, [Number(v.p) || 0]);
      }

      /* ── Reopen after coming back from checkout ───────────────────── */

      rememberReopen(item) {
        // Modo diferido: no se ha añadido nada, así que no hay nada que reabrir.
        // Guardar el pack pendiente haría que «Volver» desde el checkout
        // resucitara un modal que no corresponde a ninguna línea del carrito.
        if (this._deferPending) {
          this.clearReopen();
          return;
        }
        try {
          sessionStorage.setItem(
            'ntl_am_reopen',
            JSON.stringify({ item, t: Date.now(), path: location.pathname + location.search })
          );
        } catch (e) {
          /* storage blocked */
        }
      }

      clearReopen() {
        try {
          sessionStorage.removeItem('ntl_am_reopen');
        } catch (e) {
          /* storage blocked */
        }
      }

      /**
       * Going to checkout and pressing Back should land the shopper back on the
       * open modal, not on a silent page. Two routes home:
       *  - bfcache restore: the DOM survives, so the modal is often still open;
       *    just resync it against the real cart.
       *  - full reload: rebuild from the sessionStorage breadcrumb.
       */
      bindReopen() {
        if (!this.cfg.enabled || !this.cfg.reopenOnBack) return;

        window.addEventListener('pageshow', (e) => {
          if (e.persisted && this.isOpen) {
            this.resyncAfterReturn();
            return;
          }
          if (e.persisted) this.restoreReopen();
        });

        this.restoreReopen();
      }

      async restoreReopen() {
        if (this.isOpen) return;
        let saved = null;
        try {
          saved = JSON.parse(sessionStorage.getItem('ntl_am_reopen') || 'null');
        } catch (e) {
          return;
        }
        if (!saved?.item) return;
        // Only for the page it was opened on, and only for this browsing session.
        if (saved.path && saved.path !== location.pathname + location.search) return;
        if (Date.now() - (saved.t || 0) > 3600000) {
          this.clearReopen();
          return;
        }

        this._reopened = true;
        const cart = await this.fetchCart();
        this.open(saved.item, cart);
      }

      /** Re-filters and re-prices against the cart as it stands now. */
      async resyncAfterReturn() {
        this._reopened = true;
        this._pending = false;
        clearTimeout(this._pendingTimer);
        if (this.cta) {
          this.cta.classList.remove('is-loading');
          this.cta.disabled = false;
        }
        clearTimeout(this._navTimer);
        this._navigating = false;
        this.isAdding = false;
        this.armSuppression();
        const cart = await this.fetchCart();
        this.cartState = cart;
        this.markInCart(cart);
        this.applyCartLinePrice(this._lastItem);
        this.syncHeaderFreqs();
            this.syncHeaderOptions();
        this.paintPackSummary();
        this.updateTotals();
        this.refreshCartCount(cart);
      }

      /**
       * Flags offers already sitting in the cart. On a reopen we deliberately
       * keep them visible (excludeInCart is bypassed) so the shopper can take
       * one back out — otherwise Back from checkout shows a modal with nothing
       * actionable in it.
       */
      markInCart(cart) {
        const lines = new Map();
        (cart?.items || []).forEach((li) => {
          if (!lines.has(Number(li.product_id))) lines.set(Number(li.product_id), li);
        });
        this.visibleCards().forEach((card) => {
          const line = lines.get(Number(card.dataset.productId));
          if (line) {
            card.classList.add('is-in-cart', 'is-selected');
            card.dataset.lineKey = line.key;
          } else {
            card.classList.remove('is-in-cart');
            delete card.dataset.lineKey;
          }
          this.syncToggleA11y(card);
        });
      }

      /** Removes an offer's line from the cart (shopper-facing cart edit). */
      async removeFromCart(card) {
        const key = card.dataset.lineKey;
        if (!key) return;
        this._ownRequest = true;
        try {
          const res = await fetch(this.route('cart/change.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
          const cart = await res.json();
          card.classList.remove('is-in-cart', 'is-selected');
          delete card.dataset.lineKey;
          this.syncToggleA11y(card);
          this.refreshCartCount(cart);
          this.cartState = cart;
          window.upcartRefreshCart?.();
          this.updateTotals();
          this.track('remove_from_cart', [card]);
        } catch (err) {
          console.error('[ntl-am] remove failed:', err);
          this.setError(this.cfg.errorGeneric || '');
        } finally {
          this._ownRequest = false;
        }
      }

      allowCart() {
        document.documentElement.classList.remove('ntl-am-suppress-cart');
        this.unlockScroll();
      }

      /* ── Frequency cap ───────────────────────────────────────────── */

      capKey() {
        return 'ntl_am_shown';
      }

      withinFrequencyCap() {
        const cap = this.cfg.frequencyCap;
        if (!cap || cap === 'always') return true;
        try {
          const store = cap === 'once_per_session' ? sessionStorage : localStorage;
          const raw = store.getItem(this.capKey());
          if (!raw) return true;
          if (cap === 'once_per_session') return false;
          return Date.now() - parseInt(raw, 10) > 86400000;
        } catch (e) {
          return true;
        }
      }

      markShown() {
        const cap = this.cfg.frequencyCap;
        if (!cap || cap === 'always') return;
        try {
          const store = cap === 'once_per_session' ? sessionStorage : localStorage;
          store.setItem(this.capKey(), String(Date.now()));
        } catch (e) {
          /* storage blocked */
        }
      }

      /* ── Open / close ────────────────────────────────────────────── */

      /** @returns {boolean} true when the modal was actually shown. */
      open(item, cart) {
        this.cartState = cart;
        this._lastItem = item;
        // Set BEFORE anything reads this.twoSteps. The id was only a local in
        // prepareOffers(), which runs later than the layout decision would need.
        this.addedProductId = Number(item?.product_id ?? item?.id ?? 0) || null;
        // Apertura nueva: el paso vuelve a decidirlo el modal hasta que el
        // cliente toque «Continuar» o «Volver».
        this._userStepped = false;
        const visible = this.prepareOffers(item, cart);
        if (!visible.length && this.cfg.hideWhenEmpty) return false;
        // With no offers left we still show the confirmation, the frequency
        // pills and the exits — falling back to the cart drawer here was the
        // other reason it appeared on add.
        this.classList.toggle('ntl-am--no-offers', visible.length === 0);
        if (this._reopened) this.markInCart(cart);

        this.fillAddedItem(item);
        // Landings: fill the runtime controls for whatever was added. Not
        // awaited — open() is synchronous — and cached per handle, so the
        // second call from the instant-open reconcile costs nothing.
        this.ensureHeaderControls(item);
        this.setError('');
        this.updateTotals();

        this.applyLayoutMode();

        this.lastFocus = document.activeElement;
        this.isOpen = true;
        this._openedAt = Date.now();
        this.suppressCart();
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        this.lockScroll();
        document.addEventListener('keydown', this.onKeydown);
        requestAnimationFrame(() => this.querySelector('.ntl-am__close')?.focus());

        this.markShown();
        this.track('view', visible);
        return true;
      }

      /**
       * A transformed / sticky ancestor would clip a position:fixed overlay.
       * Reparent to <body> once, outside the theme editor (where the section
       * wrapper gets re-rendered and would orphan the node).
       */
      escapeStackingContext() {
        if (window.Shopify?.designMode) return;
        if (this.parentElement === document.body) return;
        document.body.appendChild(this);
      }

      openPreview() {
        this.applyLayoutMode();
        this.querySelectorAll('.ntl-am__offer').forEach((c) => (c.hidden = false));
        this.preselect();
        this.updateTotals();
        this.isOpen = true;
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
      }

      close() {
        this.isOpen = false;
        this._pending = false;
        this._instantOwn = false;
        this._prearmed = false;
        // Cerrar antes de «Continuar» no añade nada: se suelta la selección
        // pendiente y el carrito se queda exactamente como estaba. Nada ha
        // tocado el guard de duplicados ni el contador, porque nada se añadió.
        this._deferPending = false;
        this._pendingAdd = null;
        this._committing = false;
        clearTimeout(this._prearmTimer);
        clearTimeout(this._pendingTimer);
        // Un modal cerrado no tiene nada en vuelo que sostener: si se cierra
        // mientras el CTA estaba «añadiendo» (o esperando una navegación que no
        // llegó), soltarlo aquí evita reabrirlo con el botón muerto —
        // `isAdding` en alto hace que onAddSelection salga por la primera línea.
        clearTimeout(this._navTimer);
        this._navigating = false;
        this.isAdding = false;
        if (this.cta) {
          this.cta.classList.remove('is-loading');
          this.cta.disabled = false;
        }
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.removeEventListener('keydown', this.onKeydown);
        this.allowCart();
        this.clearReopen();
        this.lastFocus?.focus?.();
      }

      onKeydown(e) {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
          return;
        }
        if (e.key !== 'Tab') return;
        const focusables = [...this.dialog.querySelectorAll('button:not([disabled]), [href], select, [tabindex]:not([tabindex="-1"])')].filter(
          (el) => el.offsetParent !== null
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      /* ── Added item header ───────────────────────────────────────── */

      /* ── Page product (PDP selection / manual pick) ──────────────── */

      bindPageVariantSync() {
        if (!this.pageProduct) return;
        // Fired by product-info.js after the variant swap on the PDP.
        document.addEventListener('ntlVariantUpdated', (e) => {
          if (this.isOpen) return;
          this.applyPageProduct(e.detail?.variantId);
        });
        this.headerVariantId = Number(this.resolvePageVariantId()) || null;
        this.applyPageProduct(this.headerVariantId);
        this.syncHeaderOptions();
      }

      /** The variant currently chosen in the page's add-to-cart form. */
      resolvePageVariantId() {
        const input = document.querySelector(
          'product-form form [name="id"], form[action*="/cart/add"] [name="id"]'
        );
        return input?.value || this.pageProduct?.variantId;
      }

      /** Paints the header from the page/manual product instead of a cart line. */
      applyPageProduct(variantId) {
        const wrap = this.querySelector('.ntl-am__added');
        if (!wrap || !this.pageProduct) return;
        const variants = this.pageProduct.variants || [];
        const v = variants.find((x) => String(x.id) === String(variantId)) || variants[0];
        if (!v) return;

        const img = wrap.querySelector('img');
        if (img && v.img) {
          img.src = v.img;
          img.alt = this.pageProduct.title || '';
        }

        const titleEl = wrap.querySelector('.ntl-am__added-title');
        if (titleEl) titleEl.textContent = this.pageProduct.title || '';

        const subEl = wrap.querySelector('.ntl-am__added-sub');
        if (subEl) {
          const parts = [];
          if (this.pageProduct.subtitle) parts.push(this.pageProduct.subtitle);
          if (v.t && v.t !== 'Default Title') parts.push(v.t);
          subEl.textContent = parts.join(' · ');
        }

        const priceEl = wrap.querySelector('.ntl-am__added-price');
        if (priceEl) priceEl.textContent = this.money(v.p);

        const compareEl = wrap.querySelector('.ntl-am__added-compare');
        if (compareEl) {
          compareEl.textContent = this.money(v.c);
          compareEl.hidden = !(v.c > v.p);
        }

        // Tercer sitio que pinta el precio de cabecera: que la base del CTA lo
        // siga aquí también. Inerte fuera de la vista de pack.
        this.rememberHeaderAmount(v.p, v.c);
      }

      /* ── Frequency pills on the added product ────────────────────── */

      /** The cart line the header is showing. */
      headerLine() {
        // Modo diferido: el pack todavía no está en el carrito, así que no hay
        // línea que sincronizar — y una línea previa del mismo pack no habla de
        // lo que se está configurando ahora. Devolver null aquí es lo que
        // neutraliza de un tiro syncHeaderFreqs y los swap* (que salen en falso).
        if (this._deferPending) return null;
        const item =
          this._lastItem ||
          (this.pageProduct
            ? { product_id: this.pageProduct.id, id: this.resolvePageVariantId() }
            : null);
        if (!item) return null;
        const lines = this.collectProductLines(item);
        return lines.length ? this.matchLine(item, lines) : null;
      }

      /* -- Runtime-built header controls (landings) ------------------ */

      /**
       * On a PDP, Liquid renders the frequency pills and variant selectors from
       * the page's product. On a landing there is no `product` object, and a
       * landing with several packs cannot pin one in the section settings
       * either: selling plan ids differ per product, so a fixed one would apply
       * a plan that does not belong to the pack being added (Shopify rejects it)
       * and would show frequencies that pack does not have.
       *
       * So when Liquid left a mount point, the controls are built here from the
       * product that was ACTUALLY added, fetched from /products/{handle}.js.
       */
      async ensureHeaderControls(item) {
        const mount = this.querySelector('.ntl-am__added-dynamic');
        if (!mount) return; // PDP - Liquid already rendered them
        const handle = item && item.handle;
        if (!handle) {
          mount.hidden = true;
          this.togglePackConfig(mount);
          return;
        }

        const product = await this.fetchProduct(handle);
        if (!product) {
          // Degrade quietly: the modal keeps working as cross-sell only.
          mount.innerHTML = '';
          mount.hidden = true;
          this.togglePackConfig(mount);
          // Sin producto no habrá precio nunca: suelta el esqueleto para que no
          // se quede girando. «Continuar» sigue añadiendo lo que pedía la LP.
          if (this._deferPending) this.paintPendingPrice();
          return;
        }

        this.headerProduct = this.toHeaderProduct(product, item);
        this.headerVariantId = Number(item.id) || this.headerProduct.variantId;

        const html = this.buildHeaderControls(this.headerProduct, item);
        mount.innerHTML = html;
        mount.hidden = !html;
        this.togglePackConfig(mount);

        this.sortAllFreqs();
        this.syncHeaderOptions();
        this.syncHeaderFreqs();
        // Los selectores acaban de existir: ya hay algo que resumir.
        this.paintPackSummary();
        // Now that the real product is known, the static header block can show
        // the pack's name + image even when the UpCart event arrived without
        // them (see paintAddedIdentity). Gated per instance.
        this.paintAddedIdentity();
        // El tachado «sin suscripción» y su píldora de % necesitan el producto,
        // que hasta esta línea no existía: `open()` pinta el precio ANTES de
        // llamar aquí (no puede esperar a la red). Repintado con los mismos
        // datos de carrito, así que si aún no hay ninguno no hace nada. Acotado
        // al flag para que friegasuelos siga con un solo pintado.
        if (this.cfg.headerPackShowIdentity) this.applyCartLinePrice(this._lastItem);
        // Modo diferido: no hay línea que leer y el producto acaba de llegar, así
        // que es AQUÍ donde el precio (con su tachado, su píldora de % y el total
        // del CTA) se pinta por primera vez de verdad.
        if (this._deferPending) this.paintPendingPrice();
        // open() decides the step BEFORE this resolves — it cannot await, the
        // whole point of opening on the click is not waiting for the network.
        // With the controls still unbuilt, step1HasContent() saw nothing to
        // configure and the modal skipped straight to the offers. Decided again
        // now that they exist, unless the shopper already moved on themselves.
        if (!this._userStepped) this.applyLayoutMode();
      }

      /**
       * La tarjeta de configuración (vista de pack) vive y muere con su mount.
       * Su rótulo vive fuera de la tarjeta, así que se apaga con ella: un
       * «Elige cómo quieres tu pack» sin nada debajo sería peor que nada.
       */
      togglePackConfig(mount) {
        const wrap = mount.closest('.ntl-am__pack-config');
        if (!wrap) return;
        wrap.hidden = mount.hidden;
        const title = this.querySelector('.ntl-am__pack-config-title');
        if (title) title.hidden = mount.hidden;
      }

      /**
       * Paints name + image of the added product into the static `.ntl-am__added`
       * block from the product fetched for the controls (this.headerProduct).
       *
       * Why: on a landing a pack that needs BOTH a variant and a frequency is
       * added straight from the LP button, and the UpCart event for these bundles
       * can arrive without `title`/`image`, so fillAddedItem paints them blank.
       * ensureHeaderControls already fetched /products/{handle}.js for the
       * controls, so title + per-variant image are available here — the image
       * follows the currently selected variant (headerVariantId), falling back to
       * the product's featured image (variant.img already carries that fallback).
       *
       * Gated behind `headerPackShowIdentity` so only the Bolas LP opts in. PDPs
       * never reach ensureHeaderControls' body (no `.ntl-am__added-dynamic` mount)
       * and `page_product` mode pins its own identity, so both stay untouched.
       */
      paintAddedIdentity() {
        if (!this.cfg.headerPackShowIdentity) return;
        const data = this.headerProduct;
        if (!data) return;
        const wrap = this.querySelector('.ntl-am__added');
        if (!wrap) return;

        const variant =
          (data.variants || []).find((v) => Number(v.id) === Number(this.headerVariantId)) ||
          (data.variants || [])[0];

        const img = wrap.querySelector('img');
        const src = (variant && variant.img) || (data.variants[0] && data.variants[0].img) || null;
        if (img && src) {
          img.src = this.sized(src, 220);
          img.alt = data.title || '';
        }

        const titleEl = wrap.querySelector('.ntl-am__added-title');
        // A merchant-set override still wins; otherwise use the real catalog name.
        if (titleEl && !this.cfg.addedTitleOverride && data.title) {
          titleEl.textContent = data.title;
        }
      }

      /**
       * Resumen de lo configurado en el paso 1, dentro de la tarjeta del pack.
       *
       * En el paso 2 la tarjeta de configuración se oculta (CSS), así que sin
       * esto el cliente perdería de vista lo que acaba de elegir justo cuando
       * se le pide una decisión más. Se pinta como texto plano —una línea por
       * set de opciones más la cadencia— debajo del precio y el envío.
       *
       * La fuente es el DOM de los propios selectores, que siguen montados
       * aunque estén ocultos: así el resumen no puede discrepar de lo elegido,
       * y volver con «← Volver», cambiar fragancia o plan y seguir otra vez lo
       * repinta sin estado propio que mantener.
       *
       * Acotado a `headerPackShowIdentity`: friegasuelos y las PDPs salen en la
       * primera línea y nunca llegan a crear el elemento.
       */
      paintPackSummary() {
        if (!this.cfg.headerPackShowIdentity) return;
        const wrap = this.querySelector('.ntl-am__added');
        if (!wrap) return;

        // Nombre visible de cada fila: el del componente del pack cuando hay
        // bloque `pack_component` que encaje ("Eco-Tiras de Detergente"), y si
        // no el nombre crudo del set de opciones ("Fragancia").
        const names = this.headerData?.options || [];
        const rows = [];
        this.querySelectorAll('.ntl-am__added-opt-block').forEach((block) => {
          const on = block.querySelector('.ntl-am__added-opt.is-selected');
          const value = on && on.dataset.optionValue;
          if (!value) return;
          const optName = names[Number(block.dataset.optionIndex)] || '';
          const comp = this.packComponentFor(optName);
          rows.push([(comp && comp.name) || optName, value]);
        });

        // Cadencia: el nombre del plan elegido, o la etiqueta de compra única
        // cuando la píldora marcada no lleva plan.
        const pill = this.querySelector('.ntl-am__added-freq.is-selected');
        if (pill) {
          const plan = (pill.dataset.planName || '').trim();
          if (plan) rows.push([this.cfg.packSummaryFreqLabel || this.cfg.headerFreqLabel || '', plan]);
          else if (this.cfg.otpLabel) rows.push(['', this.cfg.otpLabel]);
        }

        let box = wrap.querySelector('.ntl-am__pack-summary');
        if (!rows.length) {
          // Un pack sin opciones ni planes no tiene nada que resumir.
          if (box) box.hidden = true;
          return;
        }
        if (!box) {
          box = document.createElement('div');
          box.className = 'ntl-am__pack-summary';
          wrap.appendChild(box);
        }
        box.hidden = false;
        box.innerHTML = rows
          .map(
            ([label, value]) =>
              '<p class="ntl-am__pack-summary-row">' +
              (label
                ? '<span class="ntl-am__pack-summary-label">' + this.esc(label) + ':</span> '
                : '') +
              '<span class="ntl-am__pack-summary-value">' +
              this.esc(value) +
              '</span></p>'
          )
          .join('');
      }

      /** Cached per handle - the same pack gets re-opened often. */
      async fetchProduct(handle) {
        this._productCache = this._productCache || {};
        if (this._productCache[handle]) return this._productCache[handle];
        try {
          const res = await fetch(this.route('products/' + handle + '.js'), {
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const data = await res.json();
          this._productCache[handle] = data;
          return data;
        } catch (err) {
          // Not cached, so a later open can retry.
          console.warn('[ntl-am] no se pudo cargar /products/' + handle + '.js', err);
          return null;
        }
      }

      /** products/{handle}.js -> the shape the option logic already speaks. */
      toHeaderProduct(p, item) {
        const variants = p.variants || [];
        const first = variants.find((v) => v.available) || variants[0];
        const planNames = {};
        // CA-16 PT: solo planes de JUO, sin el grupo 'Build-a-box' y con
        // entregas recurrentes. Mismo filtro que snippets/ntl-atc-modal-juo-plan-ids.liquid.
        const validPlans = new Set();
        (p.selling_plan_groups || []).forEach((g) => {
          if (g.app_id !== 'juo-subscriptions' || g.name === 'Build-a-box') return;
          (g.selling_plans || []).forEach((sp) => {
            if (sp.recurring_deliveries === false) return;
            planNames[sp.id] = sp.name;
            validPlans.add(String(sp.id));
          });
        });
        const allocs = (v) =>
          (v.selling_plan_allocations || []).filter((a) => validPlans.has(String(a.selling_plan_id)));
        return {
          id: p.id,
          handle: p.handle,
          title: p.title,
          variantId: Number(item && item.id) || (first && first.id) || null,
          // products.js returns option objects in recent API versions and plain
          // strings in older ones.
          options: (p.options || []).map((o) => (typeof o === 'string' ? o : o.name)),
          planNames,
          variants: variants.map((v) => ({
            id: v.id,
            t: v.title,
            p: v.price,
            c: v.compare_at_price || 0,
            a: v.available !== false,
            opts: v.options || [],
            plans: allocs(v).map((a) => a.selling_plan_id),
            // Precio por entrega de cada plan. Es lo único que permite enseñar
            // el precio de suscripción del pack ANTES de que exista línea de
            // carrito (modo diferido) — la variante solo sabe el de compra
            // única. Mapa plan → céntimos, con `price` como respaldo.
            planPrices: Object.fromEntries(
              allocs(v).map((a) => [
                String(a.selling_plan_id),
                a.per_delivery_price != null ? a.per_delivery_price : a.price,
              ])
            ),
            img: (v.featured_image && v.featured_image.src) || p.featured_image || null,
          })),
        };
      }

      /**
       * Same markup Liquid produces, so the existing CSS, click delegation and
       * sync methods all apply unchanged.
       *
       * Values render as circles when every value of that row is in the
       * value → image map, and as pills otherwise. The map is keyed by option
       * VALUE rather than by variant on purpose: a variant carries a single
       * image, which cannot serve a product with two option sets — bundle 3
       * needs a different circle on each row.
       */
      buildHeaderControls(data, item) {
        const parts = [];
        const variant =
          data.variants.find((v) => Number(v.id) === Number(this.headerVariantId)) ||
          data.variants[0];

        if (this.cfg.headerShowVariants && data.variants.length > 1) {
          data.options.forEach((name, i) => {
            const values = [];
            data.variants.forEach((v) => {
              const val = v.opts[i];
              if (val != null && !values.includes(val)) values.push(val);
            });
            if (values.length < 2) return;
            const current = variant && variant.opts ? variant.opts[i] : null;
            const label = this.cfg.swatchShowValue
              ? this.esc(name) +
                ': <span class="ntl-am__field-value" data-opt-value>' +
                this.esc(current || '') +
                '</span>'
              : this.esc(name);
            // Qué producto se está eligiendo en esta fila. No está en los datos:
            // el pack es un único producto de Shopify y sus variantes no dicen
            // que "Fragancia del Detergente" hable de las Eco-Tiras, así que la
            // identidad viene de los bloques `pack_component` de la sección.
            //
            // Con identidad, la fila se monta como la del producto añadido:
            // foto a la izquierda (mismo tamaño que la del pack) y, a su
            // derecha, una columna con el nombre arriba y los selectores
            // debajo. Sin identidad — o con la vista de pack apagada — se
            // devuelve la fila de siempre, byte a byte.
            const comp = this.packComponentFor(name);
            const compImg =
              comp && comp.img
                ? '<img class="ntl-am__pack-comp-img" src="' +
                  this.esc(comp.img) +
                  '" alt="" loading="lazy" decoding="async">'
                : '';
            const compName = comp
              ? '<span class="ntl-am__pack-comp-name">' + this.esc(comp.name || '') + '</span>'
              : '';
            // Circles only when the whole row has an image — a mixed row of
            // circles and initials reads worse than plain pills.
            const images = this.cfg.swatchImages || {};
            const asSwatches = values.every((v) => !!images[v]);
            // Diseño amplio: en vez de círculos sueltos, la píldora con icono y
            // nombre de la buybox de la PDP. Solo cuando toda la fila tiene
            // imagen (una fila mezclada de iconos y texto se lee peor) y solo en
            // `--roomy`: det40 y pastillas siguen con sus círculos de siempre.
            const asVpills = asSwatches && this.classList.contains('ntl-am--roomy');
            // Color por fragancia: el mapa global manda sobre el del componente, para
            // que la misma fragancia salga igual en todas las filas (Floral rosa en
            // las Eco-Tiras y en las Perlas).
            const colors = { ...((comp && comp.colors) || {}), ...(this.cfg.fragranceColors || {}) };
            const safeColor = (c) => (/^(#[0-9a-f]{3,8}|rgba?\([0-9 ,.%]+\))$/i.test(String(c || '').trim()) ? String(c).trim() : '');
            const buttons = values
              .map((v) => {
                const on = String(v) === String(current) ? ' is-selected' : '';
                const common =
                  ' data-option-index="' + i + '" data-option-value="' + this.esc(v) +
                  '" title="' + this.esc(v) + '"';
                if (asVpills) {
                  return (
                    '<button type="button" class="ntl-am__vpill ntl-am__added-opt' + on + '"' +
                    common +
                    (safeColor(colors[v]) ? ' style="--vis-color:' + this.esc(safeColor(colors[v])) + '"' : '') +
                    '>' +
                    '<span class="ntl-am__vpill-ico">' +
                    '<img src="' + this.esc(images[v]) + '" alt="" loading="lazy" decoding="async">' +
                    '</span>' +
                    '<span class="ntl-am__vpill-name">' + this.esc(v) + '</span>' +
                    '</button>'
                  );
                }
                if (!asSwatches) {
                  return (
                    '<button type="button" class="ntl-am__freq ntl-am__added-opt' + on + '"' +
                    common + '>' + this.esc(v) + '</button>'
                  );
                }
                return (
                  '<button type="button" class="ntl-am__swatch ntl-am__added-opt' + on + '"' +
                  common + ' aria-label="' + this.esc(v) + '">' +
                  '<span class="ntl-am__swatch-ring">' +
                  '<img src="' + this.esc(images[v]) + '" alt="" loading="lazy" decoding="async">' +
                  '<span class="ntl-am__swatch-soldout" aria-hidden="true"></span>' +
                  '</span></button>'
                );
              })
              .join('');
            const controls =
              '<span class="ntl-am__field-label">' +
              label +
              '</span><div class="ntl-am__added-opts' +
              (asVpills ? ' ntl-am__added-opts--vpills' : asSwatches ? '' : ' ntl-am__added-opts--pills') +
              '" role="group" aria-label="' +
              this.esc(name) +
              '">' +
              buttons +
              '</div>';
            // La clase `--comp` es la que engancha el layout de dos columnas en
            // CSS, así que sin identidad no se emite y la fila queda idéntica.
            parts.push(
              '<div class="ntl-am__added-opt-block' +
                (comp ? ' ntl-am__added-opt-block--comp' : '') +
                '" data-option-index="' +
                i +
                '">' +
                (comp
                  ? compImg + '<div class="ntl-am__pack-comp-body">' + compName + controls + '</div>'
                  : controls) +
                '</div>'
            );
          });
        }

        // No plans on this product (bundle 1) -> no frequency block at all.
        const planIds = (variant && variant.plans) || [];
        if (this.cfg.headerShowFreq && planIds.length) {
          const currentPlan = String((item && item.selling_plan_id) || '');
          const pills = planIds.map((id) => {
            const name = data.planNames[id] || String(id);
            const text = this.cfg.freqLabelMode === 'full' ? name : this.shortPlan(name);
            return (
              '<button type="button" class="ntl-am__freq ntl-am__added-freq' +
              (String(id) === currentPlan ? ' is-selected' : '') +
              '" data-plan-id="' +
              id +
              '" data-plan-name="' +
              this.esc(name) +
              '" title="' +
              this.esc(name) +
              '">' +
              this.esc(text) +
              '</button>'
            );
          });
          // header_show_otp, not the offer cards' freq_show_otp.
          if (this.cfg.headerShowOtp) {
            pills.push(
              '<button type="button" class="ntl-am__freq ntl-am__added-freq' +
                (currentPlan ? '' : ' is-selected') +
                '" data-plan-id="" data-plan-name="">' +
                this.esc(this.cfg.otpLabel || '') +
                '</button>'
            );
          }
          // Etiqueta por pack ("Recibe un recambio de X y Y cada:") con la
          // etiqueta de siempre como fallback — ver packFreqLabel().
          const freqLabelText = this.packFreqLabel(data);
          const freqLabel = freqLabelText
            ? '<span class="ntl-am__field-label">' + this.esc(freqLabelText) + '</span>'
            : '';
          parts.push(
            '<div class="ntl-am__added-freq-block">' +
              freqLabel +
              '<div class="ntl-am__added-freqs" role="group" aria-label="' +
              this.esc(freqLabelText || '') +
              '">' +
              pills.join('') +
              '</div><span class="ntl-am__save" hidden></span></div>'
          );
        }

        return parts.join('');
      }

      /**
       * El bloque `pack_component` cuyo `option_match` aparece en el nombre del
       * set de opciones, sin distinguir mayúsculas ("detergente" encaja con
       * "Fragancia del Detergente"). Sin bloques configurados el mapa llega
       * vacío y esto devuelve null, así que la fila se pinta como siempre.
       *
       * Gana el PRIMER bloque que encaje, en su orden del editor — los match
       * específicos ("detergente", "perlas") van antes que un comodín
       * ("fragancia") para packs cuya única opción se llama solo "Fragancia".
       */
      packComponentFor(optionName) {
        if (!this.cfg.headerPackShowIdentity) return null;
        const map = this.cfg.packComponents;
        if (!map || !optionName) return null;
        const haystack = String(optionName).toLowerCase();
        const key = Object.keys(map).find((k) => k && haystack.includes(k));
        const comp = key ? map[key] : null;
        // Bloque a medio configurar (sin producto ni nombre): mejor la fila de
        // siempre que una foto vacía con un hueco al lado.
        if (!comp || (!comp.name && !comp.img)) return null;
        return comp;
      }

      /**
       * Etiqueta de frecuencia específica del pack: la plantilla configurada
       * ("Recibe un recambio de {productos} cada:") con {productos} relleno con
       * los nombres cortos de los componentes que encajan, unidos por «y» —
       * pack de 2 → "Eco-Tiras", pack de 3 → "Eco-Tiras y Perlas". Sin
       * plantilla o sin componentes que encajen, la etiqueta de siempre.
       */
      packFreqLabel(data) {
        const tpl = this.cfg.packFreqLabelTemplate;
        const fallback = this.cfg.headerFreqLabel || '';
        if (!this.cfg.headerPackShowIdentity || !tpl || !data) return fallback;
        const shorts = [];
        (data.options || []).forEach((name) => {
          const comp = this.packComponentFor(name);
          const s = comp && (comp.short || comp.name);
          if (s && !shorts.includes(s)) shorts.push(s);
        });
        if (!shorts.length) return fallback;
        return tpl.replace('{productos}', shorts.join(' y '));
      }

      /* ── Variant selectors for the added product ─────────────────── */

      /** Current combination, one value per option set, read from the DOM. */
      headerSelection() {
        const sel = [];
        this.querySelectorAll('.ntl-am__added-opt-block').forEach((block) => {
          const i = Number(block.dataset.optionIndex);
          const on = block.querySelector('.ntl-am__added-opt.is-selected');
          sel[i] = on ? on.dataset.optionValue : null;
        });
        return sel;
      }

      /**
       * On a landing the product is fetched at runtime; on a PDP it comes from
       * Liquid. Everything downstream reads through here so neither cares.
       */
      get headerData() {
        return this.headerProduct || this.pageProduct;
      }

      /** The variant matching a full combination, or null if it doesn't exist. */
      variantForOptions(values) {
        return (
          (this.headerData?.variants || []).find((v) =>
            (v.opts || []).every(
              (val, i) => values[i] == null || String(val) === String(values[i])
            )
          ) || null
        );
      }

      /** The combination currently in the cart, or whatever the DOM shows. */
      headerBaseOptions() {
        const current = (this.headerData?.variants || []).find(
          (v) => Number(v.id) === Number(this.headerVariantId)
        );
        return current?.opts ? [...current.opts] : this.headerSelection();
      }

      /**
       * Paints selected / disabled state across every option set.
       *
       * A value is disabled when no AVAILABLE variant carries it together with
       * the current values of the OTHER sets — i.e. that combination either
       * doesn't exist or is out of stock. With 9 combinations that is the only
       * honest way to show it: you can't grey out a "combination", only the
       * values that would lead to one.
       */
      syncHeaderOptions() {
        const blocks = [...this.querySelectorAll('.ntl-am__added-opt-block')];
        if (!blocks.length) return;
        const variants = this.headerData?.variants || [];
        const sel = this.headerBaseOptions();

        blocks.forEach((block) => {
          const i = Number(block.dataset.optionIndex);
          block.querySelectorAll('.ntl-am__added-opt').forEach((btn) => {
            const value = btn.dataset.optionValue;
            btn.classList.toggle('is-selected', String(sel[i]) === String(value));

            const probe = sel.slice();
            probe[i] = value;
            const reachable = variants.some(
              (v) =>
                v.a !== false &&
                (v.opts || []).every((val, j) => String(val) === String(probe[j]))
            );
            btn.classList.toggle('is-disabled', !reachable);
            if (reachable) {
              btn.removeAttribute('aria-disabled');
              btn.removeAttribute('tabindex');
            } else {
              btn.setAttribute('aria-disabled', 'true');
              btn.setAttribute('tabindex', '-1');
            }
          });
          const label = block.querySelector('[data-opt-value]');
          if (label && sel[i] != null) label.textContent = sel[i];
        });
      }

      async onHeaderOption(btn) {
        if (this._swapping || btn.classList.contains('is-disabled')) return;

        const i = Number(btn.dataset.optionIndex);
        const wanted = this.headerBaseOptions();
        wanted[i] = btn.dataset.optionValue;

        const variant = this.variantForOptions(wanted);
        // Disabled values are already filtered out, so this only trips if the
        // catalog changed under us. Abort rather than touch the cart.
        if (!variant || variant.a === false) {
          this.syncHeaderOptions();
          return;
        }

        // Optimistic marking so the tap answers instantly. syncHeaderOptions
        // repaints from the real cart line when the swap lands — and puts the
        // previous value back if it failed.
        btn.closest('.ntl-am__added-opt-block')
          .querySelectorAll('.ntl-am__added-opt')
          .forEach((b) => b.classList.toggle('is-selected', b === btn));

        this.track('header_variant_change', []);
        // Modo diferido: no hay línea que reescribir, solo la selección que se
        // enviará al pulsar «Continuar».
        if (this._deferPending) {
          this.setPendingVariant(variant.id);
          return;
        }
        const ok = await this.swapHeaderVariant(variant.id);
        if (!ok) this.syncHeaderOptions();
      }

      /** Marks the pill that matches what's actually in the cart. */
      syncHeaderFreqs() {
        const wrap = this.querySelector('.ntl-am__added-freqs');
        if (!wrap) return;
        const line = this.headerLine();
        if (!line) return; // no line yet — keep the server-rendered default
        const current = String(line.selling_plan_allocation?.selling_plan?.id || '');
        wrap.querySelectorAll('.ntl-am__added-freq').forEach((p) => {
          p.classList.toggle('is-selected', (p.dataset.planId || '') === current);
        });
      }

      /** Forces the configured frequency once, if the line differs. */
      async applyHeaderDefaultPlan() {
        if (!this.cfg.headerApplyDefaultPlan || this._headerDefaultApplied) return;
        const wrap = this.querySelector('.ntl-am__added-freqs');
        const want = (this.cfg.headerDefaultPlan || '').trim().toLowerCase();
        if (!wrap || !want) return;

        const target = [...wrap.querySelectorAll('.ntl-am__added-freq')].find(
          (p) => (p.dataset.planName || '').trim().toLowerCase() === want
        );
        if (!target) {
          // Never guess a frequency the merchant didn't ask for.
          console.warn(
            '[ntl-am] el producto no tiene el plan "' + this.cfg.headerDefaultPlan + '" — no se fuerza nada'
          );
          return;
        }
        this._headerDefaultApplied = true;
        const line = this.headerLine();
        const current = String(line?.selling_plan_allocation?.selling_plan?.id || '');
        if (current === target.dataset.planId) return;
        await this.swapHeaderPlan(target.dataset.planId);
      }

      onHeaderFreq(pill) {
        if (this._swapping) return;
        const wrap = pill.parentElement;
        const previous = wrap.querySelector('.ntl-am__added-freq.is-selected');
        wrap
          .querySelectorAll('.ntl-am__added-freq')
          .forEach((p) => p.classList.toggle('is-selected', p === pill));
        this.track('header_plan_change', []);
        // Modo diferido: la píldora marcada ES la elección, no un reflejo de la
        // línea del carrito. Nada que reescribir, así que nada que revertir.
        if (this._deferPending) {
          this.setPendingPlan(pill.dataset.planId || '');
          return;
        }
        this.swapHeaderPlan(pill.dataset.planId || '').then((ok) => {
          if (!ok && previous) {
            wrap
              .querySelectorAll('.ntl-am__added-freq')
              .forEach((p) => p.classList.toggle('is-selected', p === previous));
          }
        });
      }

      /**
       * Moves the already-added line to another frequency.
       *
       * /cart/change.js cannot switch a line to a different selling plan —
       * measured 422 on this store; it only accepts clearing one. So the line
       * has to be re-added with the new plan and the old one removed.
       *
       * Two things this order protects against, both reproduced on the LP:
       *  · Removing before adding empties the cart when the add fails (a plan id
       *    that doesn't belong to the variant returns 422). So: add first, and
       *    only remove once the add is confirmed.
       *  · The old line's key goes stale the moment the cart changes. Removing
       *    with the pre-add key returns 400 and leaves BOTH lines in the cart,
       *    so the cart is re-read to get the current key.
       */
      /**
       * Plan change — a thin wrapper over swapHeaderLine so the two kinds of
       * change (frequency and variant) share one implementation.
       */
      async swapHeaderPlan(planId) {
        const line = this.headerLine();
        if (!line) return false;
        const current = String(line.selling_plan_allocation?.selling_plan?.id || '');
        if (current === String(planId || '')) return true;
        return this.swapHeaderLine({
          variantId: line.variant_id ?? line.id,
          planId: planId || null,
          source: 'ntl-atc-modal-freq',
        });
      }

      /** Variant change, keeping whatever plan is currently selected. */
      async swapHeaderVariant(variantId) {
        const line = this.headerLine();
        if (!line) return false;
        if (String(line.variant_id ?? line.id) === String(variantId)) return true;
        return this.swapHeaderLine({
          variantId,
          planId: line.selling_plan_allocation?.selling_plan?.id || null,
          source: 'ntl-atc-modal-variant',
        });
      }

      /**
       * Rewrites the added line as a different variant and/or plan.
       *
       * /cart/change.js cannot move a line to another selling plan (measured 422
       * on this store) and cannot change its variant at all, so the line has to
       * be re-added and the old one removed.
       *
       * Order is what keeps the cart safe, and both hazards were reproduced here:
       *  · Removing first empties the cart when the add fails (an invalid
       *    variant/plan pair returns 422). So: add first, remove only once the
       *    add is confirmed.
       *  · The old line's key changes the moment the cart does (84c35f… →
       *    e918cb…) and Shopify PREPENDS the new line, so neither the old key
       *    nor the old index can be reused — the cart is re-read in between.
       */
      async swapHeaderLine({ variantId, planId, source }) {
        const line = this.headerLine();
        if (!line) return false;
        const fromVariant = String(line.variant_id ?? line.id);
        const fromPlan = String(line.selling_plan_allocation?.selling_plan?.id || '');

        this._swapping = true;
        this._ownRequest = true;
        this.setError('');
        // Controls stay clickable and only the price shows a skeleton: the swap
        // needs ~1.5s of network no matter what, but grinding the UI to a halt
        // is what made it feel frozen.
        this.markPricePending();

        try {
          const payload = {
            id: Number(variantId),
            quantity: line.quantity,
            // Carry the original properties over — the LP's buybox writes a
            // visible "Friegas" property we must not drop.
            properties: { ...(line.properties || {}), _source: source },
          };
          if (planId) payload.selling_plan = Number(planId);

          const res = await fetch(this.route('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [payload] }),
          });
          const data = await res.json();
          if (!res.ok || data.status) {
            // Nothing has been removed yet, so the cart is exactly as it was.
            this.setError(data.description || this.cfg.errorGeneric || '');
            return false;
          }

          // CA-16 PT (portado de afc52d1): pinta desde esta respuesta, que ya
          // trae el precio real con descuento de la línea nueva. La limpieza de
          // abajo (re-fetch + quitar la línea vieja) solo importa para el
          // checkout, no para el precio que se enseña.
          const newLine = (data.items && data.items[0]) || null;
          if (newLine) {
            const qty = Number(newLine.quantity) || 1;
            const original = newLine.original_line_price ?? 0;
            this.paintHeaderPrice(
              newLine.final_line_price ?? 0,
              original,
              !!newLine.selling_plan_allocation,
              qty,
              [qty > 0 ? original / qty : original]
            );
          }

          // The LINE key of what we just added. In Shopify's cart JSON `id` is
          // the VARIANT id and `key` is the line — guarding with `id` meant that
          // on a plain plan change (same variant) the guard matched the old line
          // too, `stale` came back undefined, nothing was removed and the pack
          // ended up duplicated.
          const addedKey = String(data.items?.[0]?.key || data.key || '');

          const mid = await this.fetchCart();
          const stale = (mid?.items || []).find(
            (li) =>
              String(li.variant_id ?? li.id) === fromVariant &&
              String(li.selling_plan_allocation?.selling_plan?.id || '') === fromPlan &&
              // Either the variant or the plan has changed, so the pair above
              // already excludes the new line; the key is just a belt-and-braces
              // check that we never delete the one we just created.
              (!addedKey || String(li.key || '') !== addedKey)
          );

          let cart = mid;
          if (stale) {
            // change.js returns the whole cart, so this doubles as the refresh —
            // one round trip less (~400ms off a ~2s operation).
            const rm = await fetch(this.route('cart/change.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: stale.key, quantity: 0 }),
            });
            if (rm.ok) {
              const removed = await rm.json();
              if (Array.isArray(removed?.items)) cart = removed;
            } else {
              // The new line is in and the old one is not out: visible in the
              // cart and recoverable, never a silent loss. Say so.
              this.setError(this.cfg.errorGeneric || '');
            }
          }

          this.cartState = cart;
          this.headerVariantId = Number(variantId);
          if (this._lastItem) {
            this._lastItem.id = Number(variantId);
            this._lastItem.selling_plan_id = planId ? Number(planId) : null;
          }
          this.refreshCartCount(cart);
          this.applyPageProduct(variantId);
          this.applyCartLinePrice(this._lastItem);
          this.syncHeaderFreqs();
          this.syncHeaderOptions();
          // Keep the header image in sync with the newly selected variant.
          this.paintAddedIdentity();
          // Y el resumen del paso 2 con la fragancia / frecuencia recién puestas.
          this.paintPackSummary();
          window.upcartRefreshCart?.();
          return true;
        } catch (err) {
          console.error('[ntl-am] line swap failed:', err);
          this.setError(this.cfg.errorGeneric || '');
          return false;
        } finally {
          this._swapping = false;
          this._ownRequest = false;
          this.querySelector('.ntl-am__added')?.classList.remove('is-price-pending');
        }
      }

      fillAddedItem(item) {
        // "page_product" mode pins the header to the page / manually picked
        // product, so the added cart line must not overwrite it.
        if (this.cfg.addedSource === 'page_product' && this.pageProduct) {
          this.applyPageProduct(this.resolvePageVariantId());
          // Identity comes from the page product, but the price still has to
          // reflect the plan and any discount code — see applyCartLinePrice.
          this.applyCartLinePrice(item);
          return;
        }

        const wrap = this.querySelector('.ntl-am__added');
        if (!wrap || !item) return;

        const img = wrap.querySelector('img');
        const src = item.featured_image?.url || item.image;
        if (img && src) {
          img.src = this.sized(src, 220);
          img.alt = item.featured_image?.alt || item.product_title || '';
        } else if (img && !this.cfg.headerPackShowIdentity) {
          // With headerPackShowIdentity on, packs whose UpCart event lacks an
          // image get one painted from the fetched product (paintAddedIdentity),
          // which in the reconcile path runs BEFORE this — so don't wipe it.
          img.removeAttribute('src');
        }

        const titleEl = wrap.querySelector('.ntl-am__added-title');
        if (titleEl) {
          // Same reasoning: fall back to the fetched product name so a title-less
          // pack event doesn't blank out what paintAddedIdentity already set.
          const identityFallback = this.cfg.headerPackShowIdentity
            ? this.headerProduct?.title || ''
            : '';
          titleEl.textContent =
            this.cfg.addedTitleOverride || item.product_title || item.title || identityFallback;
        }

        const subEl = wrap.querySelector('.ntl-am__added-sub');
        if (subEl) {
          // Vista de pack: la variante se elige justo debajo, en la tarjeta de
          // configuración, y repetirla aquí ("Floral / Floral") solo ensucia.
          // La cantidad tampoco pinta nada cuando el precio que se enseña es el
          // del pack. Queda la frecuencia, si la hay; sin nada, la línea se va.
          const packView = !!this.cfg.headerPackShowIdentity;
          const parts = [];
          if (!packView && item.variant_title && item.variant_title !== 'Default Title') {
            parts.push(item.variant_title);
          }
          if (item.selling_plan_allocation?.selling_plan?.name) {
            parts.push(item.selling_plan_allocation.selling_plan.name);
          }
          if (!packView && this.cfg.addedShowQty && item.quantity > 1) parts.push('x' + item.quantity);
          subEl.textContent = parts.join(' · ');
          if (packView) subEl.hidden = !parts.length;
        }

        const shownFinal = this.headerAmount(
          item.final_line_price,
          item.final_price ?? item.price,
          item.quantity
        );
        const shownOriginal = this.headerAmount(
          item.original_line_price,
          item.original_price,
          item.quantity
        );

        // Mismo criterio que en `paintHeaderPrice`: con suscripción, el tachado
        // es el precio sin suscripción. `isSub` solo se calcula en la vista de
        // pack para no meter una lectura del carrito en el camino de las PDPs.
        const isSub = this.cfg.headerPackShowIdentity ? this.isSubscription(item) : false;
        const subRef = this.headerSubReference(isSub);
        const shownCompare = subRef > shownOriginal ? subRef : shownOriginal;

        const priceEl = wrap.querySelector('.ntl-am__added-price');
        if (priceEl) priceEl.textContent = this.money(shownFinal);

        const compareEl = wrap.querySelector('.ntl-am__added-compare');
        if (compareEl) {
          const final = this.headerAmount(item.final_line_price, item.final_price, item.quantity);
          compareEl.textContent = this.money(shownCompare);
          compareEl.hidden = !(shownCompare > final);
        }
        this.paintAddedPct(shownFinal, shownCompare, isSub);

        // El importe que se acaba de pintar es también la base del total del
        // CTA en la vista de pack (packPlanTotal): un cálculo, dos lectores.
        this.rememberHeaderAmount(shownFinal, shownCompare);
        this.applyCartLinePrice(item);
      }

      /**
       * El importe que enseña la cabecera.
       *
       * Por defecto es el total de la línea (`*_line_price`), que ya incluye la
       * cantidad. En la vista de pack la cabecera no habla de la línea sino del
       * PACK, así que se reparte entre sus unidades: lo que se lee es lo que
       * cuesta un pack — por entrega si va en suscripción. Con `lineTotal`
       * ausente se devuelve el campo por unidad de cart.js tal cual, que ya
       * viene sin multiplicar.
       *
       * Con la vista de pack apagada devuelve exactamente `lineTotal ?? unit ?? 0`,
       * es decir, lo mismo que se pintaba antes de existir este método.
       */
      headerAmount(lineTotal, unit, qty) {
        if (lineTotal == null) return unit ?? 0;
        if (!this.cfg.headerPackShowIdentity) return lineTotal;
        const units = Number(qty) || 0;
        // Redondeo a céntimos: Shopify reparte los descuentos fijos con resto,
        // así que la división rara vez cae exacta.
        return units > 1 ? Math.round(lineTotal / units) : lineTotal;
      }

      /**
       * Guarda el importe unitario que la cabecera ACABA de pintar, para que el
       * total del CTA no vuelva a deducirlo por su cuenta (y no pueda discrepar
       * de lo que el cliente está leyendo justo encima). Lo llaman los tres
       * sitios que pintan ese precio: `fillAddedItem`, `paintHeaderPrice` y
       * `applyPageProduct`.
       *
       * Solo la vista de pack lo lee — con el flag apagado sale sin escribir
       * nada y el camino de friegasuelos/PDPs queda igual que antes.
       */
      rememberHeaderAmount(final, original) {
        if (!this.cfg.headerPackShowIdentity) return;
        const unit = Math.max(0, Number(final) || 0);
        this._headerUnit = unit;
        this._headerUnitCompare = Math.max(unit, Number(original) || 0);
      }

      /**
       * Vista de pack: lo que cuesta el PLAN que se está montando — el pack
       * recién añadido a su precio unitario (el mismo que enseña la cabecera:
       * por entrega si va en suscripción, ya con el descuento aplicado) más las
       * ofertas marcadas, a su variante/plan elegidos. Nada más.
       *
       * Antes el CTA salía de `projectedTotal()`, que arranca del total del
       * CARRITO: con cosas de antes dentro anunciaba «874,94 €» para un pack de
       * 24 €, y con el pack añadido x2 lo contaba dos veces. Para el envío
       * gratis se sigue usando el carrito real — ahí sí cuenta todo lo que el
       * cliente va a pagar.
       */
      packPlanTotal(offersTotal) {
        return (this._headerUnit || 0) + (Number(offersTotal) || 0);
      }

      /** El tachado que le corresponde: mismo criterio, precios sin descuento. */
      packPlanCompare(offersCompare) {
        return (this._headerUnitCompare || 0) + (Number(offersCompare) || 0);
      }

      /**
       * Repaints the header price from the real cart line.
       *
       * Neither the catalog price nor the variant price knows about the selling
       * plan or about discount codes. This LP applies FRIEGA2026SUB via
       * fetch('/discount/CODE'), which lands as a line-level allocation — it is
       * absent from cart.discount_applications, so the only trustworthy figures
       * are the line's own original_price / final_price. Measured on the LP:
       * price 720, final_price 630 with a 720 allocation titled FRIEGA2026SUB
       * spread over the line (a fixed "one box free").
       *
       * Prices are per-unit in cart.js, so we scale by the quantity added rather
       * than using *_line_price, which covers the whole existing line.
       */
      applyCartLinePrice(item) {
        if (this.cfg.priceSource !== 'cart_line' || !item) return false;
        // Modo diferido: no hay línea, y la del mismo pack de una compra
        // anterior pintaría un precio que no es el que se está configurando.
        // Ahí manda paintPendingPrice, que lee el producto.
        if (this._deferPending) return false;

        // Prefer the real cart: buying the same product one-off AND on
        // subscription creates TWO separate lines, and a single discount code
        // only lands on one of them. Measured on the LP with 1 one-off + 2 subs:
        // the one-off line was 0,00 € carrying the whole 9,00 € discount while
        // the subscription line was 14,40 € with none — so reading one line hid
        // the free box completely. Summing every line of the product is the only
        // figure that matches what the shopper actually pays for it.
        const lines = this.collectProductLines(item);
        if (lines.length) {
          const scoped = this.cfg.priceScope === 'product' ? lines : [this.matchLine(item, lines)].filter(Boolean);
          if (scoped.length) {
            const original = scoped.reduce((n, l) => n + (l.original_line_price ?? 0), 0);
            const final = scoped.reduce((n, l) => n + (l.final_line_price ?? 0), 0);
            const qty = scoped.reduce((n, l) => n + (l.quantity ?? 0), 0);
            const unitPrices = scoped.map((l) => l.original_price ?? l.price ?? 0);
            return this.paintHeaderPrice(final, original, this.isSubscription(item), qty, unitPrices);
          }
        }

        // No usable cart yet (first paint): the UpCart event is already
        // discount-aware, so paint from it and let the background fetch refine.
        if (item._discountAware) {
          const qty = Number(item.quantity) || 1;
          const original = item.original_line_price ?? 0;
          return this.paintHeaderPrice(
            item.final_line_price ?? 0,
            original,
            this.isSubscription(item),
            qty,
            [qty > 0 ? original / qty : original]
          );
        }
        return false;
      }

      /** Every cart line for this item's product, with real price fields. */
      collectProductLines(item) {
        const cart = this.cartState;
        if (!cart?.items?.length || !item) return [];
        return cart.items.filter(
          (li) =>
            Number(li.product_id) === Number(item.product_id) &&
            li.final_line_price != null &&
            li.original_line_price != null
        );
      }

      /** The one line that matches this item's variant AND purchase type. */
      matchLine(item, lines) {
        const vid = String(item.id ?? '');
        const planId = item.selling_plan_id ? String(item.selling_plan_id) : '';
        const sameVariant = lines.filter((li) => String(li.variant_id ?? li.id) === vid);
        const pool = sameVariant.length ? sameVariant : lines;
        const byPlan = pool.find(
          (li) => String(li.selling_plan_allocation?.selling_plan?.id || '') === planId
        );
        return byPlan || pool[0] || null;
      }

      findCartLine(item) {
        const lines = this.collectProductLines(item);
        return lines.length ? this.matchLine(item, lines) : null;
      }

      isSubscription(item) {
        // Modo diferido: manda el plan que el cliente lleva elegido. Una línea
        // previa del mismo pack no puede decidir si ESTO va en suscripción.
        if (this._deferPending) return !!this._pendingAdd?.selling_plan;
        if (item?.selling_plan_allocation) return true;
        return !!this.findCartLine(item)?.selling_plan_allocation;
      }

      /** The zero-price wording differs for subscription vs one-off. */
      freeLabelFor(isSub) {
        if (isSub) return this.cfg.freeLabelSub || this.cfg.freeLabel || '';
        return this.cfg.freeLabel || '';
      }

      /**
       * Vista de pack: el precio SIN suscripción de la variante que está
       * seleccionada, para usarlo de tachado cuando el cliente elige una
       * frecuencia. Devuelve 0 —es decir, «no hay referencia»— con la vista de
       * pack apagada o en compra única, y ahí nada cambia.
       *
       * Por qué no vale lo que trae la línea del carrito: en una línea con plan,
       * `original_price` YA es el precio del plan (medido en products.js del pack
       * de 2: variante 26,45 € → asignación price/per_delivery_price 23,90 €),
       * así que `original_line_price === final_line_price` y el tachado nunca
       * aparecía. El descuento por suscribirse solo vive en la comparación entre
       * el precio de la variante y el de la asignación.
       *
       * La referencia es `v.p` (el precio de venta en compra única), NO
       * `max(v.p, v.c)`: la píldora que la acompaña dice «Descuento suscripción»
       * y medirla contra un `compare_at` metería en ese porcentaje una rebaja
       * que no tiene nada que ver con suscribirse. Con `priceSource:
       * 'cart_line'` el `compare_at` no se pintaba tampoco en compra única
       * (la línea del carrito no lo lleva), así que no se pierde nada.
       */
      headerSubReference(isSub) {
        if (!this.cfg.headerPackShowIdentity || !isSub) return 0;
        const data = this.headerProduct;
        if (!data) return 0;
        const variants = data.variants || [];
        const v =
          variants.find((x) => Number(x.id) === Number(this.headerVariantId)) || variants[0];
        return v ? Math.max(0, Number(v.p) || 0) : 0;
      }

      /**
       * «-10% Descuento suscripción» junto al precio del pack, con la misma
       * píldora que usan las tarjetas de oferta (`.ntl-am__pct`, mismo sufijo
       * configurable). Solo con una frecuencia elegida: en compra única no hay
       * descuento de suscripción que anunciar.
       *
       * El porcentaje sale de las DOS cifras que el cliente está leyendo —el
       * tachado y el precio—, así que la píldora nunca puede contradecir lo que
       * hay a su lado. Si además hubiera un descuento de código en la línea
       * (tipo FRIEGA2026SUB, «1ª caja gratis»), el hueco entre esas dos cifras
       * lo incluye y el porcentaje sube: ese caso lo explica la nota
       * `.ntl-am__added-note`, que sigue leyendo los totales reales de la línea.
       *
       * El elemento se crea aquí y no en Liquid a propósito: el mismo bloque lo
       * renderizan la LP de friegasuelos y las PDPs, y su HTML tiene que quedar
       * idéntico. Mismo patrón que `paintPackSummary`.
       */
      paintAddedPct(final, compare, isSub) {
        if (!this.cfg.headerPackShowIdentity) return;
        const row = this.querySelector('.ntl-am__added-prices');
        if (!row) return;
        let el = row.querySelector('.ntl-am__added-pct');
        const pct =
          isSub && compare > 0 && compare > final ? Math.round((1 - final / compare) * 100) : 0;
        if (pct <= 0) {
          if (el) el.hidden = true;
          return;
        }
        if (!el) {
          el = document.createElement('span');
          el.className = 'ntl-am__pct ntl-am__added-pct';
          // Detrás del precio: la píldora explica el salto entre el tachado y
          // él, y la nota de «1ª caja gratis» se queda al final de la fila.
          const priceEl = row.querySelector('.ntl-am__added-price');
          if (priceEl) priceEl.insertAdjacentElement('afterend', el);
          else row.appendChild(el);
        }
        const suffix = this.cfg.pctSuffixSub;
        el.textContent = '-' + pct + '%' + (suffix ? ' ' + suffix : '');
        el.hidden = false;
      }

      /**
       * Con compra única elegida, el precio con suscripción del pack al lado del
       * PVP: «26,45 €  [23,90 € con suscripción]». La comparación que ya hace el
       * tachado cuando hay plan (`paintAddedPct`) no existe en compra única, que
       * es como abre el modal, y ahí el cliente no veía lo que ahorra suscribiéndose.
       *
       * El importe sale de los `planPrices` de la variante elegida (por entrega,
       * ya con el descuento del plan), no de un texto escrito a mano. Solo con el
       * ajuste `plan_hint_label`: vacío, ni se crea el elemento y el resto de
       * instancias quedan como estaban. Mismo patrón que `paintAddedPct`.
       */
      paintPlanHint(isSub) {
        const label = this.cfg.planHintLabel;
        if (!label || !this.cfg.headerPackShowIdentity) return;
        const row = this.querySelector('.ntl-am__added-prices');
        if (!row) return;
        let el = row.querySelector('.ntl-am__plan-hint');
        const data = this.headerProduct;
        const variants = (data && data.variants) || [];
        const v =
          variants.find((x) => Number(x.id) === Number(this.headerVariantId)) || variants[0];
        const prices = v
          ? Object.values(v.planPrices || {}).map(Number).filter((n) => n > 0)
          : [];
        const best = prices.length ? Math.min(...prices) : 0;
        const pvp = v ? Number(v.p) || 0 : 0;
        if (isSub || !(best > 0 && best < pvp)) {
          if (el) el.hidden = true;
          return;
        }
        if (!el) {
          el = document.createElement('span');
          el.className = 'ntl-am__pct ntl-am__plan-hint';
          const priceEl = row.querySelector('.ntl-am__added-price');
          if (priceEl) priceEl.insertAdjacentElement('afterend', el);
          else row.appendChild(el);
        }
        el.textContent = this.money(best) + ' ' + label;
        el.hidden = false;
      }

      paintHeaderPrice(final, original, isSub, qty, unitPrices) {
        const wrap = this.querySelector('.ntl-am__added');
        if (!wrap) return false;
        const freeLabel = this.freeLabelFor(isSub);

        // Solo cambia lo que se PINTA: en la vista de pack, precio y tachado
        // salen por unidad para que el par siga siendo coherente. La nota de
        // "1ª caja gratis" sigue leyendo los totales reales de la línea — con
        // el ahorro repartido entre 3 unidades ya no equivaldría a una caja
        // entera y la nota desaparecería justo cuando más hace falta.
        const showFinal = this.headerAmount(final, final, qty);
        const showOriginal = this.headerAmount(original, original, qty);
        // Con una frecuencia elegida, el tachado es el precio sin suscripción
        // (ver headerSubReference). Fuera de la vista de pack —y en compra
        // única— la referencia es 0 y `showCompare` es exactamente
        // `showOriginal`, así que friegasuelos y las PDPs no notan nada.
        const subRef = this.headerSubReference(isSub);
        const showCompare = subRef > showOriginal ? subRef : showOriginal;
        // Este es el repintado bueno (sale del carrito real), así que es el que
        // manda sobre la base del total del CTA — ver packPlanTotal. El importe
        // FINAL sigue siendo el del plan (por entrega, ya con descuentos): lo
        // que cambia es solo la referencia tachada.
        this.rememberHeaderAmount(showFinal, showCompare);

        const priceEl = wrap.querySelector('.ntl-am__added-price');
        if (priceEl) {
          priceEl.textContent = showFinal <= 0 && freeLabel ? freeLabel : this.money(showFinal);
        }
        const compareEl = wrap.querySelector('.ntl-am__added-compare');
        if (compareEl) {
          compareEl.textContent = this.money(showCompare);
          compareEl.hidden = !(showCompare > showFinal);
        }
        this.paintAddedPct(showFinal, showCompare, isSub);
        this.paintPlanHint(isSub);
        this.paintFreeUnitNote(final, original, qty, unitPrices);
        this.paintShippingNote(isSub);
        wrap.classList.remove('is-price-pending');
        this.updateTotals();
        return true;
      }

      /* ── Shipping ────────────────────────────────────────────────── */

      /** Amounts come from the editor as "25" / "3,95" — accept both separators. */
      money2cents(value) {
        const n = parseFloat(String(value == null ? '' : value).replace(',', '.').trim());
        return Number.isFinite(n) ? Math.round(n * 100) : null;
      }

      get shippingThreshold() {
        return this.money2cents(this.cfg.shippingThreshold);
      }

      get shippingCost() {
        return this.money2cents(this.cfg.shippingCost) || 0;
      }

      /**
       * Lo que el pack pendiente va a sumar al carrito.
       *
       * En modo diferido (`pack_defer_add`) nada se ha añadido todavía, así que
       * `cart.total_price` NO lo incluye: con el carrito vacío, un pack de
       * 26,45 € daba un total proyectado de 0 y el umbral de envío gratis
       * (25 €) se leía como NO alcanzado — el primer paso anunciaba
       * «+3,95€ de gastos de envío» sobre un pedido que ya viaja gratis.
       *
       * El importe es el unitario que la cabecera acaba de pintar
       * (`_headerUnit`, ya por entrega y con el descuento del plan) por las
       * unidades pendientes.
       */
      pendingLineTotal() {
        if (!this._deferPending) return 0;
        const qty = Math.max(1, Number(this._pendingAdd?.quantity) || 1);
        return (Number(this._headerUnit) || 0) * qty;
      }

      /** What the cart will total once the ticked offers are added. */
      projectedTotal() {
        const cartTotal = Number(this.cartState?.total_price) || 0;
        let offers = 0;
        this.visibleCards().forEach((card) => {
          if (!card.classList.contains('is-selected') || card.classList.contains('is-in-cart')) return;
          const sel = this.cardSelection(card);
          if (sel) offers += sel.price;
        });
        // El pack pendiente cuenta para el umbral: es dinero de este mismo
        // pedido. Una vez añadido ya viaja dentro de `cartTotal`, y entonces
        // `pendingLineTotal()` devuelve 0, así que no se cuenta dos veces.
        return cartTotal + offers + this.pendingLineTotal();
      }

      /** Any subscription line in the cart, ours or the page's. */
      cartHasSubscription() {
        return (this.cartState?.items || []).some((li) => !!li.selling_plan_allocation);
      }

      hasFreeShipping(total, isSub) {
        // Business rule: a subscription always ships free, threshold or not.
        if (this.cfg.freeShippingWithSub && (isSub || this.cartHasSubscription())) return true;
        const threshold = this.shippingThreshold;
        if (threshold == null) return null; // not configured — say nothing
        return total >= threshold;
      }

      /** The line under the price: different wording for subscription vs one-off. */
      paintShippingNote(isSub) {
        const el = this.querySelector('.ntl-am__added-shipping');
        if (!el) return;
        const free = this.hasFreeShipping(this.projectedTotal(), isSub);
        let text = isSub ? this.cfg.shippingNoteSub : this.cfg.shippingNoteOtp;
        if (free && this.cfg.shippingNoteFree) text = this.cfg.shippingNoteFree;
        el.textContent = text || '';
        el.hidden = !text;
      }

      /**
       * With 2+ boxes the price stops being 0, so the offer becomes invisible:
       * the shopper sees 7,20 € and can't tell the first box was free. This adds
       * "1ª caja gratis" beside the price so the discount stays legible.
       *
       * Only claims a free unit when the saving really equals one unit's price —
       * a percentage discount would make that wording a lie, so in that case the
       * note stays hidden and only the struck-through original shows.
       */
      paintFreeUnitNote(final, original, qty, unitPrices) {
        const noteEl = this.querySelector('.ntl-am__added-note');
        if (!noteEl) return;
        const label = this.cfg.freeUnitLabel;
        const units = Number(qty) || 1;
        const saving = original - final;

        if (!label || units < 2 || saving <= 0 || final <= 0) {
          noteEl.hidden = true;
          return;
        }

        // Compare against every unit price present, not the average: a mixed
        // cart has a 9,00 one-off unit next to 7,20 subscription units, and the
        // average matches neither. Cents tolerance because Shopify rounds when
        // it spreads a fixed line discount.
        const candidates = (unitPrices && unitPrices.length ? unitPrices : [original / units]).filter(
          (p) => p > 0
        );
        const oneUnitFree = candidates.some((p) => Math.abs(saving - p) <= 2);
        noteEl.textContent = label;
        noteEl.hidden = !oneUnitFree;
      }

      /**
       * The correct price cannot exist before the add responds — measured 1279 ms
       * on the LP, all of it UpCart's POST. Rather than show the catalog price
       * and then visibly swap it for "¡Gratis!" a second later, hold the price
       * area in a skeleton state until the real figure arrives.
       */
      markPricePending() {
        if (!this.cfg.pendingPrice) return;
        this.querySelector('.ntl-am__added')?.classList.add('is-price-pending');
      }

      /**
       * "Añadiendo a la cesta" while the add is in flight, then the confirmation.
       * Fits the ~1.3s UpCart POST exactly, and turns dead waiting time into
       * feedback. A minimum duration stops it flashing when the response is fast.
       */
      enterAddingState() {
        if (!this.cfg.addingState) return;
        this._addingSince = Date.now();
        this.classList.add('is-adding');
        const titleEl = this.querySelector('.ntl-am__head-title');
        if (titleEl) {
          // Del cache, no del DOM: en la vista de pack el texto que hay ahí
          // puede ser ya el del paso 1, y guardarlo como "confirmación" haría
          // que el paso 2 heredara el título equivocado.
          if (this._confirmTitle == null) this._confirmTitle = this.headTitleBase();
          titleEl.textContent = this.cfg.addingLabel || this._confirmTitle;
        }
      }

      exitAddingState() {
        if (!this.classList.contains('is-adding')) return;
        const elapsed = Date.now() - (this._addingSince || 0);
        const min = this.cfg.addingMinMs || 0;
        const finish = () => {
          this.classList.remove('is-adding');
          const titleEl = this.querySelector('.ntl-am__head-title');
          if (titleEl && this._confirmTitle != null) titleEl.textContent = this._confirmTitle;
          // Después de restaurar, nunca antes: mientras dura el "añadiendo" ese
          // texto manda, y al soltarlo hay que devolver el del paso en curso.
          this.paintStepHeading();
        };
        if (elapsed >= min) finish();
        else setTimeout(finish, min - elapsed);
      }

      /**
       * El texto de cabecera de origen, leído del DOM una sola vez. Es la
       * referencia a la que se vuelve tanto al salir del estado "añadiendo"
       * como al pasar al paso 2, así que tiene que quedar cacheado antes de
       * que nadie lo toque.
       */
      headTitleBase() {
        if (this._headTitleBase == null) {
          this._headTitleBase = this.querySelector('.ntl-am__head-title')?.textContent || '';
        }
        return this._headTitleBase;
      }

      /**
       * Vista de pack: el paso 1 no confirma un añadido, propone terminar de
       * configurarlo ("¡Listo para añadir a tu carrito!"), y el título de
       * confirmación vuelve en el paso 2. Así el modal enseña UN solo titular
       * en cada momento en vez de dos que se contradicen.
       *
       * Con `step1_heading` vacío — el resto de instancias — sale por la
       * primera línea sin tocar el DOM.
       */
      paintStepHeading() {
        const alt = this.cfg.step1Heading;
        if (!alt) return;
        const el = this.querySelector('.ntl-am__head-title');
        if (!el) return;
        // Antes del guard de "añadiendo": el original tiene que quedar cacheado
        // aunque en este momento no se pueda pintar.
        const base = this.headTitleBase();
        if (this.classList.contains('is-adding')) return;
        el.textContent = this.dataset.modalStep === '1' ? alt : base;
      }

      sized(url, width) {
        if (!url) return url;
        return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
      }

      /* ── Offer filtering ─────────────────────────────────────────── */

      prepareOffers(item, cart) {
        const addedId = Number(item?.product_id ?? item?.id ?? 0);
        const inCart = new Set((cart?.items || []).map((li) => Number(li.product_id)));
        const visible = [];
        let slots = this.cfg.maxVisible || 99;

        this.querySelectorAll('.ntl-am__offer').forEach((card) => {
          const data = this.offersById[card.dataset.blockId];
          const pid = Number(card.dataset.productId);
          let show = !!data;

          if (show && pid === addedId) show = false;
          // On a reopen we keep in-cart offers visible on purpose, so the
          // shopper can take one back out (see markInCart).
          if (show && this.cfg.excludeInCart && !this._reopened && inCart.has(pid)) show = false;
          if (show && data.onlyFor?.length && !data.onlyFor.includes(addedId)) show = false;
          if (show && data.hideFor?.length && data.hideFor.includes(addedId)) show = false;
          if (show && !data.variants.some((v) => v.a)) show = false;
          if (show && slots <= 0) show = false;

          card.hidden = !show;
          if (show) {
            slots -= 1;
            visible.push(card);
          } else {
            card.classList.remove('is-selected');
          }
        });

        if (visible.length) this.preselect();
        return visible;
      }

      preselect() {
        const cards = this.visibleCards();
        if (!cards.length) return;
        cards.forEach((card) => {
          const data = this.offersById[card.dataset.blockId];
          card.classList.toggle('is-selected', !!data?.preselected);
        });
        if (this.cfg.preselectFirst && !cards.some((c) => c.classList.contains('is-selected'))) {
          cards[0].classList.add('is-selected');
        }
        cards.forEach((c) => {
          this.syncToggleA11y(c);
          this.setCardStep(c);
        });
      }

      visibleCards() {
        return [...this.querySelectorAll('.ntl-am__offer')].filter((c) => !c.hidden);
      }

      /* ── UI bindings ─────────────────────────────────────────────── */

      bindUI() {
        this.addEventListener('click', (e) => {
          // X and backdrop share one action (the cart drawer by default), which
          // is deliberately separate from the dismiss link's (checkout). Both
          // are tracked under their own name so GA4 can tell them apart.
          if (e.target === this) {
            // Its own action, separate from the X: with the multistep flow the
            // modal asks for interaction, and a stray click on the backdrop
            // must not throw the shopper out. Defaults to doing nothing.
            this.runExit(this.cfg.overlayAction, 'overlay');
            return;
          }

          if (e.target.closest('.ntl-am__close')) {
            this.runExit(this.cfg.closeAction, 'close_x');
            return;
          }

          const dismiss = e.target.closest('.ntl-am__dismiss');
          if (dismiss) {
            this.runExit(this.cfg.dismissAction, 'dismiss');
            return;
          }

          if (e.target.closest('.ntl-am__continue')) {
            // Modo diferido: «Continuar» ES el añadido al carrito. Al paso 2 se
            // pasa desde dentro de commitPendingAdd, cuando el pack ya ha
            // entrado — nunca antes.
            if (this._deferPending) {
              this.commitPendingAdd();
              return;
            }
            this.goToStep(2);
            return;
          }

          if (e.target.closest('.ntl-am__back')) {
            this.goToStep(1);
            return;
          }

          const addedOpt = e.target.closest('.ntl-am__added-opt');
          if (addedOpt) {
            e.stopPropagation();
            this.onHeaderOption(addedOpt);
            return;
          }

          const addedFreq = e.target.closest('.ntl-am__added-freq');
          if (addedFreq) {
            e.stopPropagation();
            this.onHeaderFreq(addedFreq);
            return;
          }

          const swatch = e.target.closest('.ntl-am__swatch');
          if (swatch) {
            e.stopPropagation();
            this.onSwatch(swatch);
            return;
          }

          const freq = e.target.closest('.ntl-am__freq');
          if (freq) {
            e.stopPropagation();
            this.onFreq(freq);
            return;
          }

          if (e.target.closest('.ntl-am__cta')) {
            this.onAddSelection();
            return;
          }

          const card = e.target.closest('.ntl-am__offer');
          if (card) this.toggleCard(card);
        });
      }

      /* ── Two-step layout ─────────────────────────────────────────── */

      /**
       * Two-step or single-step is decided per ADDED PRODUCT, so one landing can
       * open bundle 1 all at once and bundles 2 and 3 in two steps.
       *
       * The section setting stays the default: with an empty list nothing about
       * the current behaviour changes, on this page or any other.
       */
      get twoSteps() {
        const list = this.cfg.twoStepsProducts || [];
        if (!list.length) return this.cfg.layoutMode === 'two_steps';
        // No product known yet (theme editor preview): fall back to the section
        // setting rather than silently forcing a single step.
        if (!this.addedProductId) return this.cfg.layoutMode === 'two_steps';
        return list.includes(this.addedProductId);
      }

      /**
       * The two-step CSS is gated on the root class, which Liquid can only set
       * from the section setting — so with a per-product list it has to be
       * re-applied on every open, and the step attribute cleared when this
       * product opens in a single step (otherwise a stale data-modal-step from
       * a previous opening keeps half the dialog hidden).
       */
      applyLayoutMode() {
        const steps = this.twoSteps;
        this.classList.toggle('ntl-am--steps', steps);
        if (steps) {
          // Modo diferido: el paso 1 es donde se configura Y desde donde se
          // añade, así que siempre hay algo que hacer ahí. Sin esto el modal
          // abriría en el paso 2 hasta que existieran los selectores (una
          // petición después), con el CTA de la venta cruzada a un clic de un
          // carrito sin el pack dentro.
          this.dataset.modalStep = this._deferPending || this.step1HasContent() ? '1' : '2';
        } else {
          delete this.dataset.modalStep;
        }
        this.paintStepHeading();
        // El paso puede nacer ya en 2 (sin nada que configurar) o volver a 1 en
        // una reapertura: el resumen se decide con él, no solo al pulsar.
        this.paintPackSummary();
      }

      /**
       * Step 1 only earns its place if there's something to decide there. With
       * no frequency pills and no note it would be an empty screen with a
       * Continue button, so we skip straight to the offers.
       */
      step1HasContent() {
        const freq = this.querySelector('.ntl-am__added-freq-block');
        const note = this.querySelector('.ntl-am__step-note');
        return !!freq || !!note;
      }

      goToStep(step) {
        if (!this.twoSteps) return;
        // Solo se llama desde «Continuar» y «Volver», así que a partir de aquí
        // el paso lo manda el cliente: ensureHeaderControls ya no lo recalcula.
        this._userStepped = true;
        this.dataset.modalStep = String(step);
        this.paintStepHeading();
        // Al entrar en el paso 2 la configuración desaparece de la vista: el
        // resumen la sustituye, y tiene que salir con lo elegido AHORA.
        this.paintPackSummary();
        this.track(step === 1 ? 'step_1' : 'step_2', []);
        // A step change swaps most of the dialog, so put the shopper back at the
        // top instead of mid-scroll in content that no longer exists.
        this.dialog?.scrollTo?.({ top: 0 });
        this.scrollTo?.({ top: 0 });
      }

      /* ── Multistep reveal ────────────────────────────────────────── */

      /**
       * Step 0 = nothing shown · 1 = fragrance only · 2 = fragrance + frequency.
       * Cards with no fragrance to pick jump straight to 2, otherwise selecting
       * them would show nothing at all.
       */
      setCardStep(card) {
        if (!this.cfg.multistep) return;
        if (!card.classList.contains('is-selected')) {
          card.dataset.step = '0';
          return;
        }
        const hasSwatches = !!card.querySelector('.ntl-am__swatch-block');
        card.dataset.step = hasSwatches ? '1' : '2';
      }

      toggleCard(card) {
        // Already in the cart: the toggle takes it back out instead of adding
        // a duplicate line.
        if (card.classList.contains('is-in-cart')) {
          this.removeFromCart(card);
          return;
        }
        card.classList.toggle('is-selected');
        this.setCardStep(card);
        this.syncToggleA11y(card);
        this.updateTotals();
        this.track(card.classList.contains('is-selected') ? 'select' : 'deselect', [card]);
      }

      syncToggleA11y(card) {
        const btn = card.querySelector('.ntl-am__toggle');
        if (btn) btn.setAttribute('aria-pressed', String(card.classList.contains('is-selected')));
      }

      /* ── Swatch (variant) selection ──────────────────────────────── */

      onSwatch(swatch) {
        const card = swatch.closest('.ntl-am__offer');
        const data = this.offersById[card.dataset.blockId];
        if (!data) return;

        // Any swatch click advances the step — including re-clicking the one
        // already ringed. Requiring a *change* would dead-end the shopper whose
        // preferred fragrance is the preselected one: the frequency would never
        // appear and there'd be no way forward.
        if (this.cfg.multistep && card.classList.contains('is-selected')) {
          card.dataset.step = '2';
        }

        card.querySelectorAll('.ntl-am__swatch').forEach((s) => s.classList.toggle('is-selected', s === swatch));

        const variant = data.variants.find((v) => String(v.id) === swatch.dataset.variantId);
        if (!variant) return;
        card.dataset.variantId = variant.id;

        // Image
        const img = card.querySelector('.ntl-am__offer-img img');
        if (img && variant.img1) {
          img.src = variant.img1;
          img.srcset = variant.img1 + ' 1x, ' + (variant.img2 || variant.img1) + ' 2x';
        }

        // Selected value label
        const valueEl = card.querySelector('[data-swatch-value]');
        if (valueEl) valueEl.textContent = variant.t || '';

        // Subtitle keeps the configured text, appends the variant name
        const subEl = card.querySelector('.ntl-am__offer-sub');
        if (subEl) {
          const base = subEl.dataset.base || '';
          const showVariant = this.cfg.subtitleShowVariant && variant.t && variant.t !== 'Default Title';
          subEl.textContent = [base, showVariant ? variant.t : ''].filter(Boolean).join(' · ');
        }

        this.rebuildFreqs(card, variant);
        this.updateCardPrice(card);
        this.updateTotals();
        this.track('variant_change', [card]);
      }

      /** Selling plans differ per variant — rebuild the pills for the new one. */
      rebuildFreqs(card, variant) {
        const wrap = card.querySelector('.ntl-am__freqs');
        if (!wrap) return;
        const block = card.querySelector('.ntl-am__freq-block');
        const plans = variant.plans || [];

        if (!plans.length) {
          if (block) block.hidden = true;
          card.dataset.planId = '';
          return;
        }
        if (block) block.hidden = false;

        const prevName = wrap.querySelector('.ntl-am__freq.is-selected')?.dataset.planName || '';
        const showOtp = this.cfg.freqShowOtp;
        const preSub = this.offersById[card.dataset.blockId]?.preSub;

        let html = plans
          .map((p) => {
            const keep = prevName ? p.n === prevName : preSub && p === plans[0];
            return `<button type="button" class="ntl-am__freq${keep ? ' is-selected' : ''}" data-plan-id="${p.id}" data-plan-name="${this.esc(p.n)}" data-plan-price="${p.p}" title="${this.esc(p.n)}">${this.esc(
              this.cfg.freqLabelMode === 'full' ? p.n : this.shortPlan(p.n)
            )}</button>`;
          })
          .join('');

        if (showOtp) {
          const otpSelected = !preSub && !prevName;
          html += `<button type="button" class="ntl-am__freq${otpSelected ? ' is-selected' : ''}" data-plan-id="" data-plan-price="${variant.p}">${this.esc(
            this.cfg.otpLabel
          )}</button>`;
        }

        wrap.innerHTML = html;
        this.sortFreqs(wrap);
        if (!wrap.querySelector('.ntl-am__freq.is-selected')) {
          wrap.querySelector('.ntl-am__freq')?.classList.add('is-selected');
        }
        card.dataset.planId = wrap.querySelector('.ntl-am__freq.is-selected')?.dataset.planId || '';
      }

      onFreq(pill) {
        const card = pill.closest('.ntl-am__offer');
        card.querySelectorAll('.ntl-am__freq').forEach((p) => p.classList.toggle('is-selected', p === pill));
        card.dataset.planId = pill.dataset.planId || '';
        if (!card.classList.contains('is-selected')) {
          card.classList.add('is-selected');
          this.syncToggleA11y(card);
        }
        this.updateCardPrice(card);
        this.updateTotals();
        this.track('plan_change', [card]);
      }

      /**
       * "8 semanas" → "8 sem" (as in the design). Month names are already short
       * and truncating them gives broken grammar ("2 meses" → "2 mes"), so they
       * are left alone. Anything unrecognised is left alone too.
       */
      /**
       * Recharge hands the plans back in its own order (2 meses, 1 mes, 3 meses).
       * Sort them by real duration so the ladder reads 1 mes · 2 meses · 3 meses,
       * with "compra única" always last. Reordering the DOM keeps whatever was
       * preselected selected.
       */
      sortAllFreqs() {
        // Both the offer cards and the added-product block — they use different
        // wrappers, and leaving the second one out was why the header pills kept
        // Recharge's order (3 meses, 1 mes, 6 meses…).
        this.querySelectorAll('.ntl-am__freqs, .ntl-am__added-freqs').forEach((wrap) =>
          this.sortFreqs(wrap)
        );
      }

      sortFreqs(wrap) {
        if (!wrap || this.cfg.sortFrequencies === false) return;
        const pills = [...wrap.querySelectorAll('.ntl-am__freq')];
        if (pills.length < 2) return;
        pills
          .map((el) => ({
            el,
            // One-time purchase has no plan id — force it to the end.
            days: el.dataset.planId ? this.planDays(el.dataset.planName || el.textContent) : Infinity,
          }))
          .sort((a, b) => a.days - b.days)
          .forEach(({ el }) => wrap.appendChild(el));
      }

      /** Plan name → duration in days, for sorting. */
      planDays(name) {
        const n = String(name).match(/(\d+)/);
        if (!n) return Number.MAX_SAFE_INTEGER - 1;
        const num = parseInt(n[1], 10);
        const low = String(name).toLowerCase();
        if (/seman|setman|week|semaine|settiman|tygod|săptăm|tjed|týdn|εβδομ/.test(low)) return num * 7;
        if (/mes|mês|month|mois|miesi|lun|mjes|měsí|μήν/.test(low)) return num * 30;
        if (/día|dia|day|jour|giorno|dzie|zi\b/.test(low)) return num;
        return num;
      }

      shortPlan(name) {
        const num = String(name).match(/(\d+)/);
        if (!num) return name;
        if (!/seman|setman|week|semaine|settiman|tygod|săptăm|tjed|týdn|εβδομ/i.test(String(name))) {
          return name;
        }
        const lang = (document.documentElement.lang || 'es').slice(0, 2);
        const week = { es: 'sem', ca: 'set', en: 'wk', fr: 'sem', it: 'sett', pt: 'sem', pl: 'tyg', ro: 'săpt', hr: 'tj', cs: 'týd', el: 'εβδ' };
        return `${num[1]} ${week[lang] || 'sem'}`;
      }

      /* ── Pricing ─────────────────────────────────────────────────── */

      cardSelection(card) {
        const data = this.offersById[card.dataset.blockId];
        if (!data) return null;
        const vid = Number(card.dataset.variantId) || data.variants.find((v) => v.a)?.id;
        const variant = data.variants.find((v) => v.id === vid) || data.variants[0];
        if (!variant) return null;
        const planId = card.dataset.planId ? Number(card.dataset.planId) : null;
        const plan = planId ? (variant.plans || []).find((p) => p.id === planId) : null;
        const price = plan ? plan.p : variant.p;
        const compare = Math.max(variant.c || 0, variant.p);
        return { data, variant, plan, price, compare };
      }

      updateCardPrice(card) {
        const sel = this.cardSelection(card);
        if (!sel) return;
        const { variant, plan, price, compare } = sel;

        const priceEl = card.querySelector('.ntl-am__offer-price');
        if (priceEl) priceEl.textContent = this.money(price);

        const compareEl = card.querySelector('.ntl-am__offer-compare');
        if (compareEl) {
          compareEl.textContent = this.money(compare);
          compareEl.hidden = compare <= price;
        }

        const pctEl = card.querySelector('.ntl-am__pct');
        if (pctEl) {
          const pct = compare > price ? Math.round((1 - price / compare) * 100) : 0;
          // "-17%" alone doesn't say where the saving comes from — append the
          // configured wording, different for subscription vs one-off.
          const suffix = plan ? this.cfg.pctSuffixSub : this.cfg.pctSuffixOtp;
          pctEl.textContent = '-' + pct + '%' + (suffix ? ' ' + suffix : '');
          pctEl.hidden = pct <= 0;
        }

        const saveEl = card.querySelector('.ntl-am__save');
        if (saveEl) {
          if (!this.cfg.showSavings) {
            saveEl.hidden = true;
          } else {
            const saving = variant.p - price;
            const pct = variant.p > 0 && saving > 0 ? Math.round((saving / variant.p) * 100) : 0;
            if (plan && saving > 0) {
              saveEl.textContent = `${this.cfg.saveLabel} ${this.money(saving)} (-${pct}%)`;
              saveEl.hidden = false;
            } else {
              saveEl.hidden = true;
            }
          }
        }
      }

      updateTotals() {
        let total = 0;
        let compare = 0;
        let count = 0;

        this.visibleCards().forEach((card) => {
          this.updateCardPrice(card);
          if (!card.classList.contains('is-selected')) return;
          const sel = this.cardSelection(card);
          if (!sel) return;
          total += sel.price;
          compare += sel.compare;
          count += 1;
        });

        // Vista de pack: pack + ofertas, la misma cifra que enseña el CTA para
        // que las dos no puedan discrepar. Fuera de ella, las ofertas y nada
        // más — exactamente como antes.
        const packView = !!this.cfg.headerPackShowIdentity;
        const shownTotal = packView ? this.packPlanTotal(total) : total;
        const shownCompare = packView ? this.packPlanCompare(compare) : compare;

        const totalEl = this.querySelector('.ntl-am__total-price');
        if (totalEl) totalEl.textContent = this.money(shownTotal);
        const totalCompareEl = this.querySelector('.ntl-am__total-compare');
        if (totalCompareEl) {
          totalCompareEl.textContent = this.money(shownCompare);
          totalCompareEl.hidden = shownCompare <= shownTotal;
        }
        const totalRow = this.querySelector('.ntl-am__total');
        if (totalRow) totalRow.hidden = count === 0;

        // La nota de envío de la cabecera también cuenta lo marcado abajo
        // (`projectedTotal` suma las ofertas seleccionadas), así que se repinta
        // con cada marca igual que el CTA. Sin esto, desmarcar la oferta dejaba
        // «Envío gratis incluido» arriba y «+ 3,95 envío» en el CTA a la vez.
        // En la vista de pack espera a que la cabecera haya pintado su precio:
        // antes de eso el pack pendiente vale 0 y la nota saldría de pago un
        // instante.
        if (this.classList.contains('ntl-am--roomy') && (!packView || this._headerUnit > 0)) {
          this.paintShippingNote(this.isSubscription(this._lastItem));
        }

        if (this.cta) {
          // Envío: se decide con el carrito REAL (projectedTotal), porque el
          // umbral lo cruza todo lo que el cliente va a pagar, no solo el plan.
          // El IMPORTE, en cambio, es el del plan en la vista de pack — el
          // total del carrito ahí anunciaba la compra entera (ver packPlanTotal).
          const projected = this.projectedTotal();
          // Con el estado de suscripción por delante: la regla de negocio es que
          // una suscripción viaja gratis SIEMPRE, y sin este argumento el único
          // camino era `cartHasSubscription()` — falso en modo diferido, donde la
          // línea todavía no existe. El CTA anunciaba envío de pago justo debajo
          // de una cabecera que decía «Envío gratis incluido».
          const free = this.hasFreeShipping(projected, this.isSubscription(this._lastItem));
          const amount = packView ? shownTotal : projected;
          // Sin ofertas marcadas el plan sigue siendo el pack, así que la vista
          // de pack enseña su precio; el resto de instancias, nada — igual que
          // antes (`count` es un entero ≥ 0, así que `> 0` no cambia el gate).
          const priced = packView ? amount > 0 : count > 0;

          // Vista de pack: el importe va entre paréntesis pegado al label
          // ("Crear mi plan de limpieza (31,90 €)"); el resto, el "· " de siempre.
          const priceSlot = this.cta.querySelector('.ntl-am__cta-price');
          if (priceSlot) {
            priceSlot.textContent = priced
              ? (packView ? '(' + this.moneyCompact(amount) + ')' : '· ' + this.moneyCompact(amount))
              : '';
          }

          const shipSlot = this.cta.querySelector('.ntl-am__cta-shipping');
          if (shipSlot) {
            let text = '';
            if (priced && free === true) text = this.cfg.ctaShippingFree || '';
            else if (priced && free === false) {
              text = (this.cfg.ctaShippingPaid || '').replace('{envio}', this.moneyPlain(this.shippingCost));
            }
            shipSlot.textContent = text;
          }

          this.cta.disabled = count === 0;
          const label = this.cta.querySelector('.ntl-am__cta-label');
          if (label && this.cfg.ctaEmptyLabel) {
            label.textContent = count === 0 ? this.cfg.ctaEmptyLabel : this.cfg.ctaLabel;
          }
        }

        // Enlace de salida con {precio}: "Ahora no, solo quiero las bolas por
        // 13,95 €, seguir con mi carrito →". El importe es el unitario del
        // producto añadido (el mismo que pinta la card), así que se refresca
        // aquí con cada cambio de plan/variante. Sin {precio} en el texto (o
        // sin flag), el enlace queda tal cual lo sirvió Liquid.
        if (packView && this.cfg.dismissLabel && this.cfg.dismissLabel.includes('{precio}') && this._headerUnit > 0) {
          const dismissBtn = this.querySelector('.ntl-am__dismiss');
          if (dismissBtn) {
            dismissBtn.textContent = this.cfg.dismissLabel.replace('{precio}', this.money(this._headerUnit));
          }
        }
      }

      money(cents) {
        const value = (Number(cents) || 0) / 100;
        try {
          return value.toLocaleString(this.cfg.locale || 'es-ES', {
            style: 'currency',
            currency: this.cfg.currency || 'EUR',
          });
        } catch (e) {
          return value.toFixed(2).replace('.', ',') + ' €';
        }
      }

      /**
       * "16€" instead of "16,00 €" when the amount is whole — as in the design.
       * The space before the symbol is dropped too, which is what the mockup
       * shows; non-whole amounts keep the normal locale formatting.
       */
      moneyCompact(cents) {
        const value = (Number(cents) || 0) / 100;
        if (!this.cfg.ctaPriceCompact || value % 1 !== 0) return this.money(cents);
        try {
          return value
            .toLocaleString(this.cfg.locale || 'es-ES', {
              style: 'currency',
              currency: this.cfg.currency || 'EUR',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
            .replace(/\s+(?=\D*$)/, '');
        } catch (e) {
          return value + '€';
        }
      }

      /** Bare number, no currency symbol — for the {envio} token. */
      moneyPlain(cents) {
        const value = (Number(cents) || 0) / 100;
        try {
          return value.toLocaleString(this.cfg.locale || 'es-ES', {
            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          });
        } catch (e) {
          return String(value).replace('.', ',');
        }
      }

      esc(str) {
        return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      }

      /* ── Add selection ───────────────────────────────────────────── */

      async onAddSelection() {
        if (this.isAdding) return;
        const selected = this.visibleCards().filter((c) => c.classList.contains('is-selected'));
        if (!selected.length) return;

        const items = [];
        selected.forEach((card) => {
          // Ya dentro del carrito (reapertura desde el checkout, o un intento
          // anterior cuya respuesta no llegó pero sí entró): reenviarlo
          // duplicaría la línea. Cuenta como elegido, no como pendiente.
          if (card.classList.contains('is-in-cart')) return;
          const sel = this.cardSelection(card);
          if (!sel || sel.variant.a === false) return;
          const item = { id: sel.variant.id, quantity: 1, properties: { _source: 'ntl-atc-modal' } };
          if (sel.plan) item.selling_plan = sel.plan.id;
          items.push(item);
        });
        // Nada nuevo que añadir y algo ya dentro: el CTA es la salida, no un
        // segundo añadido. Sin esto el botón no haría absolutamente nada.
        if (!items.length) {
          if (selected.some((c) => c.classList.contains('is-in-cart'))) this.completeAdd(selected);
          return;
        }

        this.isAdding = true;
        this._ownRequest = true;
        this.cta.classList.add('is-loading');
        this.cta.disabled = true;
        this.setError('');

        try {
          let data = null;
          try {
            const res = await this.withTimeout(
              fetch(this.route('cart/add.js'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ items }),
              })
            );
            data = await this.withTimeout(res.json());
          } catch (err) {
            // La respuesta no ha llegado — pero el añadido sí puede haber
            // entrado (medido: el producto aparece en el carrito y el CTA se
            // queda girando). La respuesta no es la fuente de verdad, el
            // carrito sí: si está dentro se sigue por la vía buena, y solo si
            // no está se trata como fallo. Así ni se pierde una compra ya
            // hecha ni se duplica la línea reintentando.
            if (!(await this.cartContains(items))) throw err;
            console.warn('[ntl-am] la respuesta del añadido no llegó, pero está en el carrito', err);
          }

          if (data && data.status) {
            this.setError(data.description || data.message || '');
            this.cta.classList.add('is-error');
            setTimeout(() => this.cta.classList.remove('is-error'), 2000);
            return;
          }

          const cart = await this.fetchCart();
          this.refreshCartCount(cart);
          window.upcartRefreshCart?.();
          this.completeAdd(selected);
        } catch (err) {
          console.error('[ntl-am] add selection failed:', err);
          this.setError(this.cfg.errorGeneric || '');
        } finally {
          this._ownRequest = false;
          if (!this._navigating) {
            this.isAdding = false;
            this.cta.classList.remove('is-loading');
            this.cta.disabled = false;
          }
        }
      }

      /** El cierre del añadido: tracking y la salida configurada. */
      completeAdd(selected) {
        this.track('add_to_cart', selected);
        // Checkout is the default. Keep the modal and the spinner up while the
        // browser navigates — closing first flashes the page underneath.
        const action = this.cfg.afterAdd || 'checkout';
        if (action === 'checkout') {
          this.goToCheckout();
          return;
        }
        this.close();
        if (action === 'open_cart') this.openNativeCart();
      }

      /**
       * La salida al checkout, con red.
       *
       * `_navigating` sostiene el modal —y el spinner del CTA— mientras el
       * navegador se va, para que no parpadee la página de debajo. El problema
       * es lo que pasa cuando el viaje NO ocurre: en el editor de temas el
       * checkout no existe, y una extensión o una app del carrito puede
       * cancelar la navegación. Ahí ese mismo flag deja el CTA girando para
       * siempre con el producto ya en el carrito, que es exactamente el cuelgue
       * reportado.
       *
       * Igual que `instant_open_timeout` cubre la apertura optimista, aquí un
       * temporizador devuelve el control si seguimos en la página.
       */
      goToCheckout() {
        // En el editor de temas no hay checkout al que ir (la navegación se
        // queda a medias dentro del iframe): se sale por el carrito y se dice
        // por consola, en vez de dejar el modal colgado.
        if (window.Shopify?.designMode) {
          console.warn('[ntl-am] designMode: el editor no llega al checkout — se abre el carrito');
          this.close();
          this.openNativeCart();
          return;
        }
        this._navigating = true;
        // Breadcrumb so pressing Back from checkout lands on the open modal.
        if (this.cfg.reopenOnBack) this.rememberReopen(this._lastItem);
        clearTimeout(this._navTimer);
        this._navTimer = setTimeout(() => this.releaseNavigation(), this.netTimeout);
        window.location.href = this.route('checkout');
      }

      /**
       * Seguimos aquí: la navegación no ha ocurrido. Se devuelve el modal al
       * cliente en un estado usable y se marca lo que YA está en el carrito,
       * para que un segundo clic en el CTA no duplique líneas — solo reintente
       * la salida (ver el filtro `is-in-cart` de onAddSelection).
       */
      releaseNavigation() {
        if (!this._navigating) return;
        this._navigating = false;
        this.isAdding = false;
        if (this.cta) {
          this.cta.classList.remove('is-loading');
          this.cta.disabled = false;
        }
        console.warn('[ntl-am] la navegación al checkout no ocurrió — modal liberado');
        this.fetchCart().then((cart) => {
          if (!cart) return;
          this.cartState = cart;
          this.markInCart(cart);
          this.updateTotals();
        });
      }

      /**
       * Every way out of the modal funnels through here, so no exit can end up
       * as a dead end. `source` is only for tracking — the behaviour comes from
       * the action, which differs per exit (X/backdrop → cart, link → checkout).
       */
      runExit(action, source) {
        if (!action || action === 'none') return;
        this.track(source || 'dismiss', []);

        // Add diferido sin confirmar: no se añadió NADA al carrito, así que
        // salir no debe abrir el drawer ni ir a carrito/checkout — solo cerrar.
        if (this._deferPending) {
          this.close();
          return;
        }

        if (action === 'checkout') {
          // Hold the modal while the browser navigates so the page underneath
          // never flashes, and leave the breadcrumb so Back reopens it. Misma
          // red que el CTA: si la navegación no ocurre, el modal se libera.
          this.goToCheckout();
          return;
        }

        this.close();
        if (action === 'open_cart') this.openNativeCart();
        else if (action === 'go_to_cart') window.location.href = this.route('cart');
      }

      setError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = !msg;
      }

      /* ── Tracking ────────────────────────────────────────────────── */

      track(action, cards) {
        if (!window.dataLayer) return;
        window.dataLayer.push({
          event: 'ntl_atc_modal',
          ntl_atc_modal_action: action,
          ntl_atc_modal_page: window.location.pathname,
          ntl_atc_modal_offers: (cards || []).map((c) => ({
            handle: c.dataset.productHandle,
            variant_id: c.dataset.variantId || null,
            selling_plan: c.dataset.planId || 'otp',
          })),
        });
      }
    }
  );
}
